// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async'; // <-- ADD IMPORT
import ImdbSearch from './components/ImdbSearch';
import TierList from './components/TierList';
import TierListShare from './components/TierListShare';
import { DndContext, closestCenter, DragOverlay, rectIntersection, pointerWithin } from '@dnd-kit/core';
import StarterGridSelector from './components/StarterGridSelector';
import { FaArrowUp, FaGithub, FaTimes, FaSpinner, FaEdit, FaChevronLeft, FaChevronRight, FaTrashAlt } from 'react-icons/fa';
import TierSelector from './components/TierSelector';
import PositionSelector from './components/PositionSelector';
import { shouldUseTapInterface } from './utils/isMobile';
import './App.css';
import { addBadPoster, isBadPoster } from './utils/badPosterCache'; // <-- ADD IMPORT
import { CARD_DIMENSIONS } from './components/ImdbSearch'; // <-- ADD THIS IMPORT

// ---> Helper Function for Storage Namespace <---
const getCurrentStorageNamespace = () => {
  const path = window.location.pathname;
  const slugMatch = path.match(/^\/([a-zA-Z0-9\-]+-tier-list)$/);
  if (slugMatch && slugMatch[1]) {
    return slugMatch[1]; // Use the slug itself as the namespace
  }
  // Add more specific checks if needed (e.g., for /view/, though it might not store)
  // Default namespace for the root path or unrecognized paths
  return 'default'; 
};
// ---> End Helper Function <---

// Helper function to get the API base URL
const getApiBaseUrl = () => {
  // Always use relative URLs to work with proxy
  return '/api';
};

// Safe storage wrapper to handle mobile storage issues
const safeCacheStorage = {
  getItem: (key: string) => {
    try {
      // Try localStorage first
      const localValue = localStorage.getItem(key);
      if (localValue) {
        return localValue;
      }
      
      // Fall back to sessionStorage if localStorage fails or is empty
      try {
        const sessionValue = sessionStorage.getItem(key);
        if (sessionValue) {
          // console.log(`[Storage] Retrieved ${key} from sessionStorage fallback`);
          // If we got it from session, try to restore it to localStorage
          try {
            localStorage.setItem(key, sessionValue);
          } catch (e) {
            // Ignore errors when restoring to localStorage
          }
          return sessionValue;
        }
      } catch (sessionError) {
        console.error(`[Storage Error] SessionStorage fallback failed:`, sessionError);
      }
      
      return null;
    } catch (e) {
      console.error(`[Storage Error] Failed to get ${key}:`, e);
      
      // Last attempt: try sessionStorage directly
      try {
        return sessionStorage.getItem(key);
      } catch {
        return null;
      }
    }
  },
  
  setItem: (key: string, value: string) => {
    let localSuccess = false;
    let sessionSuccess = false;
    
    // Try localStorage first
    try {
      localStorage.setItem(key, value);
      localSuccess = true;
    } catch (e) {
      console.error(`[Storage Error] localStorage failed for ${key}:`, e);
      
      // If localStorage failed due to quota, try clearing some space
      if (e.name === 'QuotaExceededError' || e.code === 22) {
        try {
          // Clear less important caches first
          localStorage.removeItem('searchResultsCache');
          localStorage.removeItem('starterGridCache');
          // Try again
          try {
            localStorage.setItem(key, value);
            localSuccess = true;
          } catch (retryError) {
            console.error(`[Storage Error] localStorage retry failed:`, retryError);
          }
        } catch (clearError) {
          console.error(`[Storage Error] Failed to clear cache:`, clearError);
        }
      }
    }
    
    // Always try to save to sessionStorage as well for redundancy
    try {
      sessionStorage.setItem(key, value);
      sessionSuccess = true;
    } catch (sessionError) {
      console.error(`[Storage Error] sessionStorage failed:`, sessionError);
    }
    
    // Log storage status
    if (localSuccess && sessionSuccess) {
      // console.log(`[Storage] Successfully saved ${key} to both storage types`);
    } else if (localSuccess) {
      // console.log(`[Storage] Saved ${key} to localStorage only`);
    } else if (sessionSuccess) {
      // console.log(`[Storage] Saved ${key} to sessionStorage only`);
    } else {
      console.error(`[Storage] CRITICAL: Failed to save ${key} to any storage`);
    }
    
    return localSuccess || sessionSuccess;
  },
  
  removeItem: (key: string) => {
    let success = false;
    try {
      localStorage.removeItem(key);
      success = true;
    } catch (e) {
      console.error(`[Storage Error] Failed to remove ${key} from localStorage:`, e);
    }
    
    try {
      sessionStorage.removeItem(key);
      success = true;
    } catch (e) {
      console.error(`[Storage Error] Failed to remove ${key} from sessionStorage:`, e);
    }
    
    return success;
  }
};

// Helper function for starter grid caching
const getGridCache = (gridKey) => {
  try {
    const cache = safeCacheStorage.getItem('starterGridCache');
    if (!cache) return null;
    
    const parsed = JSON.parse(cache);
    const cachedData = parsed[gridKey];
    
    // Check if cache is still valid (less than 1 day old)
    if (cachedData && Date.now() - cachedData.timestamp < 86400000) {
      return cachedData.data;
    }
    return null;
  } catch {
    return null;
  }
};

const setGridCache = (gridKey, data) => {
  try {
    const cache = JSON.parse(safeCacheStorage.getItem('starterGridCache') || '{}');
    cache[gridKey] = {
      data,
      timestamp: Date.now()
    };
    safeCacheStorage.setItem('starterGridCache', JSON.stringify(cache));
  } catch (e) {
    console.error('Error setting grid cache:', e);
  }
};

const DEFAULT_TIERS = [
  { name: 'S', color: '#22c55e', items: [] },
  { name: 'A', color: '#a3e635', items: [] },
  { name: 'B', color: '#fde047', items: [] },
  { name: 'C', color: '#fdba74', items: [] },
  { name: 'D', color: '#ef4444', items: [] },
];

// Define the type for grid data
interface GridData {
  key: string;
  name: string;
  description: string;
  imdbIDs: string[];
}

export default function App() {
  // ---> Add top-level log <---
  // console.log('[App Init] Checking window.__PRELOADED_DATA__ at component start:', 
  //   window.__PRELOADED_DATA__ ? 'Exists' : 'Does NOT exist');
  // ---> End top-level log <---

  // Determine the correct namespace based on the current URL
  const storageNamespace = getCurrentStorageNamespace(); // Renamed for clarity

  // --> Add isReadOnlyView state <--
  const [isReadOnlyView, setIsReadOnlyView] = useState(() => {
    const path = window.location.pathname;
    const isShareView = /^\/view\/([a-zA-Z0-9_-]+)$/.test(path);
    const isSlugView = /^\/([a-zA-Z0-9\-]+-tier-list)$/.test(path);
    // Read-only if it's explicitly a /view/ URL
    return isShareView;
    // Slug views are initially editable
  });

  // Add state for left column collapse
  const [isLeftColumnCollapsed, setIsLeftColumnCollapsed] = useState(false);

  // --> Add handler for Edit button <--
  const handleEditSharedList = () => {
    if (!isReadOnlyView) return; // Safety check

    // 1. Save the current tiers (from shared list) to local storage UNDER DEFAULT NAMESPACE
    try {
      const defaultKey = 'tiers_default';
      safeCacheStorage.setItem(defaultKey, JSON.stringify(tiers));
      // console.log(`[Edit List] Saved shared tiers to local storage key: ${defaultKey}.`);
    } catch (e) {
      console.error('[Edit List] Failed to save tiers to local storage:', e);
      // Optionally show an error message to the user
      alert('Error saving list for editing. Please try again.');
      return;
    }

    // 2. Clear the share ID from the URL by navigating to home
    window.location.href = '/'; 
    // history.push('/'); // If using react-router
  };

  // State for search results and tiers
  const [searchResults, setSearchResults] = useState<any[]>([]);
  // Add filter state
  const [filterText, setFilterText] = useState('');
  // Add search query state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const imdbSearchRef = useRef<any>(null);
  // Add state for sorting search results
  const [sortCriteria, setSortCriteria] = useState({ field: 'Title', direction: 'asc' });
  // Add state to track if user explicitly sorted after search
  const [userHasSorted, setUserHasSorted] = useState(false);
  
  // IMDb list import states
  const [imdbListUrl, setImdbListUrl] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [listTitle, setListTitle] = useState('');
  
  // New state for IMDb list search
  const [imdbListSearchResults, setImdbListSearchResults] = useState<Array<{id: string, title: string, url: string}>>([]);
  const [isSearchingLists, setIsSearchingLists] = useState(false);
  const [isImportMode, setIsImportMode] = useState(true); // Track if using URL import or search selection
  // Add state to track if a search has been performed
  const [hasSearched, setHasSearched] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState('');
  
  // State for accordion sections
  const [openSection, setOpenSection] = useState<string | null>(() => {
    // Check if the current path is a shared list view
    const path = window.location.pathname;
    const isShareView = /^\/view\/([a-zA-Z0-9_-]+)$/.test(path);
    
    // If it's a share view, always start with sections collapsed
    if (isShareView) {
      return null;
    }
    
    // Original logic: Check if we have any existing data in localStorage
    const hasTiers = safeCacheStorage.getItem('tiers');
    const hasLastActiveView = safeCacheStorage.getItem('lastActiveView');
    
    // If this appears to be a clean session (no stored data), default to 'starter' section
    if (!hasTiers && !hasLastActiveView) {
      return 'starter';
    }
    
    // Otherwise, return null (no section open by default)
    return null;
  });

  // Mobile tap interface states
  const [isTapInterface, setIsTapInterface] = useState(false);
  // State for desktop users to override drag/drop with tap interface
  const [useTapInterfaceOverride, setUseTapInterfaceOverride] = useState(() => {
    // Load saved preference from localStorage, default to false (drag & drop)
    return localStorage.getItem('useTapInterfaceOverride') === 'true';
  });
  const [tierSelectorOpen, setTierSelectorOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [tapPosition, setTapPosition] = useState({ x: 0, y: 0 });
  const [sourceTierIdx, setSourceTierIdx] = useState<number | null>(null);
  const [sourceItemIdx, setSourceItemIdx] = useState<number | null>(null);
  
  // New state for position selector
  const [positionSelectorOpen, setPositionSelectorOpen] = useState(false);
  
  // State to control DragOverlay animation
  // const [disableDropAnimation, setDisableDropAnimation] = useState(false); 

  // --> Add logging here <--
  // console.log('[App Render] selectedItem:', selectedItem?.imdbID, 'tierSelectorOpen:', tierSelectorOpen);

  // Add state to track if searchResults have been populated 
  // and need measurement refresh
  const [needsMeasurementRefresh, setNeedsMeasurementRefresh] = useState(false);
  const initialMeasurementDone = useRef(false);

  // Clear any stale tap interface state on initial load
  useEffect(() => {
    // console.log('[App] Initializing and cleaning up any stale state');
    setTierSelectorOpen(false);
    setSelectedItem(null);
    setSourceTierIdx(null);
    setSourceItemIdx(null);
  }, []);

  // Helper function to get cached item from search results
  const getCachedItem = (imdbID: string) => {
    try {
      const cache = safeCacheStorage.getItem('searchResultsCache');
      if (!cache) return null;
      
      const parsed = JSON.parse(cache);
      // Check each query's results for the item
      for (const query in parsed) {
        const results = parsed[query];
        const found = results.find((item: any) => item.imdbID === imdbID);
        if (found) return found;
      }
      return null;
    } catch {
      return null;
    }
  };

  // Add state for loading shared lists
  const [loadingSharedList, setLoadingSharedList] = useState(true); // Start true to check URL initially
  const [sharedListError, setSharedListError] = useState('');
  const isSharedListLoaded = useRef(false); // Flag to prevent localStorage override after shared load
  const searchResultsContainerRef = useRef<HTMLDivElement>(null); // Ref for the scrollable container
  const [showBackToTop, setShowBackToTop] = useState(false); // State for button visibility

  const [tiers, setTiers] = useState(() => {
    // Determine the correct namespace based on the current URL
    const namespace = getCurrentStorageNamespace();
    const key = `tiers_${namespace}`;
    // console.log(`[Init State] Reading ${key} for tiers`);

    // Initial state set to default, will be overridden by localStorage or shared list
    // If it's a read-only view, don't load from localStorage yet
    if (/^\/view\/([a-zA-Z0-9_-]+)$/.test(window.location.pathname)) {
      return DEFAULT_TIERS; // Will be replaced by shared data
    }
    // Otherwise, load from the namespaced local storage key
    const saved = safeCacheStorage.getItem(key); 
    if (saved) {
      try {
        const parsedTiers = JSON.parse(saved);
        return parsedTiers.map((tier: any) => ({
          ...tier,
          items: tier.items.map((item: any) => ({ ...item, fromTier: true }))
        }));
      } catch (error) {
        console.error("Error parsing tiers from localStorage:", error);
        return DEFAULT_TIERS;
      }
    }
    return DEFAULT_TIERS;
  });
  // Log tiers state *after* it's declared
  // console.log('[App Render] Current Tiers State:', JSON.stringify(tiers.map(t => ({ name: t.name, items: t.items.map(i => i.imdbID) })))); // Log tier structure on render

  // Detect if we should use tap interface on mount
  useEffect(() => {
    setIsTapInterface(shouldUseTapInterface());
  }, []);

  // Add state to store last active search/grid info
  const [lastActiveView, setLastActiveView] = useState(() => {
    const namespace = getCurrentStorageNamespace();
    const key = `lastActiveView_${namespace}`;
    // console.log(`[Init State] Reading ${key} for lastActiveView`);
    //console.log(`[Init State] Attempting to initialize lastActiveView state...`); // ADD LOG

    // --> Check for preloaded data first <--
    if (window.__PRELOADED_DATA__) {
      //console.log('[Preload] Found preloaded data object:', window.__PRELOADED_DATA__);
      // Construct the initial state directly from preloaded data
      const { listTitle: preloadedTitle, items: preloadedItems, type: preloadedType, slug: preloadedSlug } = window.__PRELOADED_DATA__;
      const preloadedState = {
            type: preloadedType || 'apiSlug', // Default type if not provided
            slug: preloadedSlug,
            listTitle: preloadedTitle,
            items: preloadedItems,
            // Note: We don't call setListTitle here, just return the state shape
      };
      //console.log('[Init State - Preload] Setting initial lastActiveView state from preloaded data:', preloadedState);  
      return preloadedState; // Return the state derived from preloaded data

      // ---> Removed setListTitle call from here, should be handled in effects <---
      /*
      if (preloadedTitle) {
        setListTitle(preloadedTitle);
      }
      */
    }
    
    // ---> If NO preloaded data, try reading from storage <--- 
    //console.log(`[Init State - Storage] No preloaded data. Reading from storage key: ${key}`);
    const saved = safeCacheStorage.getItem(key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        //console.log('[Init State - Storage] Successfully parsed lastActiveView from storage:', parsed);
        return parsed; // Return the parsed data from storage
      } catch (error) {
        //console.error(`[Init State - Storage] Error parsing lastActiveView from storage key ${key}:`, error);
        safeCacheStorage.removeItem(key); // Remove corrupted data
        return null; // Fallback to null if parsing fails
      }
    }

    // ---> If nothing found, default to null <--- 
    //console.log(`[Init State - Storage] No data found in storage for key: ${key}. Initializing lastActiveView to null.`);
    return null; // Default to null if nothing found in preload or storage
  });

  // Effect to load shared list from URL or local storage on mount
  useEffect(() => {
    const loadData = async () => {
      setLoadingSharedList(true); // Start loading indicator
      const currentPath = window.location.pathname;
      const slugMatch = currentPath.match(/^\/([a-zA-Z0-9\-]+-tier-list)$/);
      const currentSlug = slugMatch ? slugMatch[1] : null;

      // ---> DEV MODE SLUG HANDLING <--- // Rename section
      // ---> SLUG URL HANDLING (Dev & Prod via API Fetch) <---
      // if (import.meta.env.DEV && currentSlug && !window.__PRELOADED_DATA__) { // Remove dev check and preload check
      if (currentSlug) { // If it's a slug path, always try to fetch
          //console.log(`[Slug Load] Detected slug "${currentSlug}", fetching data via API...`);
          try {
            const res = await fetch(`/api/list-by-slug/${currentSlug}`);
            if (!res.ok) {
              let errorData = {};
              try { errorData = await res.json(); } catch { errorData = { error: res.statusText }; }
              throw new Error(errorData.error || `API fetch failed: ${res.status}`);
            }
            const data = await res.json();
            //console.log('[Dev Slug Load] Received data:', data);

            if (data.listTitle) setListTitle(data.listTitle);
            if (data.items && Array.isArray(data.items)) {
                setSearchResults(data.items);
                setNeedsMeasurementRefresh(true); // ---> Re-enable this
                // Set lastActiveView based on fetched data
                // /* // Remove start comment
                setLastActiveView({
                    type: 'apiSlug', // Use a distinct type for dev mode loading
                    slug: data.slug,
                    listTitle: data.listTitle,
                    items: data.items
                });
                // */ // Remove end comment
            }
            setOpenSection('import'); // Open the import section

          } catch (err) {
            //console.error('[Dev Slug Load] Error fetching list data by slug:', err);
            setSharedListError(`Failed to load list data: ${err.message}`); // Use sharedListError state for feedback
            // Maybe load default state or show error message?
            loadFromLocalStorage(); // Fallback to default local storage for the slug? Or just error out.
          }
          setLoadingSharedList(false);
          return; // Stop further loading attempts
      }
      // ---> END SLUG URL HANDLING <---

      // Check for PRODUCTION preloaded data (after dev check)
      // ---> Remove Production Preload Check <---
      /*
      if (window.__PRELOADED_DATA__) {
        console.log('[Preload] Found preloaded data object:', window.__PRELOADED_DATA__);
// ... (rest of the preload block) ...
        delete window.__PRELOADED_DATA__; 
        setLoadingSharedList(false);
        return; // Stop further loading attempts
      }
      */
      // ---> End Remove Production Preload Check <---

      // --> CHECK FOR PRELOADED DATA FIRST <-- // Remove this duplicate block
      /*
      if (window.__PRELOADED_DATA__) {
// ... (rest of the duplicate preload block) ...
        return; // Stop further loading attempts
      }
      */
      // --> END PRELOADED DATA CHECK <--

      // Existing logic for read-only view (share ID)
      if (isReadOnlyView) {
        const path = window.location.pathname;
        const match = path.match(/^\/view\/([a-zA-Z0-9_-]+)$/);
        const shareId = match ? match[1] : null;

        if (shareId) {
          // console.log(`[Share Load - ReadOnly] Found share ID in URL: ${shareId}`);
          try {
            const response = await fetch(`${getApiBaseUrl()}/share?id=${shareId}`);
            if (!response.ok) {
              let errorData = {};
              try {
                errorData = await response.json();
              } catch {
                errorData = { error: response.statusText };
              }
              throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }
            const sharedTiers = await response.json();
            if (sharedTiers && Array.isArray(sharedTiers)) {
              // console.log('[Share Load - ReadOnly] Successfully fetched shared list data.');
              setTiers(sharedTiers); // Set state with shared data
              isSharedListLoaded.current = true; // Mark as loaded from share

              // Restore last active view (optional for read-only, maybe just show the list?)
              // For now, let's keep it simple and not restore search/grid on read-only view
            } else {
              throw new Error('Invalid data received for shared list.');
            }
          } catch (e) {
            console.error('[Share Load - ReadOnly] Error loading shared list:', e);
            setSharedListError(e instanceof Error ? e.message : 'Could not load shared list.');
            // Don't fallback to localStorage in read-only mode
          }
        } else {
          // Should not happen if isReadOnlyView is true based on regex, but handle defensively
          console.error("[Share Load - ReadOnly] Mismatch: isReadOnlyView is true, but no share ID found.");
          setSharedListError('Invalid shared list URL.');
        }
      } else {
        // It's the main editing view, load from localStorage
        // console.log('[Load Data - Editable] Loading from localStorage.');
        loadFromLocalStorage(); // Tiers already loaded initially, this ensures consistency? Or remove initial load. Let's refine.
        // Re-evaluate initial state loading for tiers to simplify.

        // Restore last active search/grid after loading tiers
        // Only restore if NOT preloaded
        if (!window.__PRELOADED_DATA__) { // Check again just in case
           await restoreLastActiveView(); // Await this to ensure data is ready
        }
      }
      setLoadingSharedList(false); // Done checking/loading
    };

    loadData();
  }, [isReadOnlyView]); // Re-run effect if isReadOnlyView changes (e.g., after "Edit" click + redirect)


  // Function to restore the last active search or grid
  const restoreLastActiveView = async () => {
    // ---> REMOVE initial fallback block checking sessionStorage <---
    /*
    if (!lastActiveView) {
      // ... removed fallback code ...
      return;
    }
    */
    // ---> Ensure we actually have a lastActiveView object from storage <---
    if (!lastActiveView) {
      //console.log('[LastActiveView] No lastActiveView data found in state or storage. Cannot restore.');
      return; // Exit if no view data is available
    }
    
    //console.log('[LastActiveView] Attempting to restore:', lastActiveView); // Log the object we're trying to restore
    
    if (lastActiveView.type === 'search' && lastActiveView.query) {
      // Restore search (Keep existing logic, seems reasonable)
      setSearchQuery(lastActiveView.query);
      setSearchLoading(true);
      //console.log(`[LastActiveView Search Restore] Restoring search for query: ${lastActiveView.query}`); // ADD LOG
      
      try {
        const cached = getCachedSearchResults(lastActiveView.query);
        if (cached) {
          //console.log(`[LastActiveView Search Restore] Found cached results for: ${lastActiveView.query}`); // ADD LOG
          setSearchResults(cached);
          setNeedsMeasurementRefresh(true); // Trigger measurement refresh
        } else {
          //console.log(`[LastActiveView Search Restore] No cache, fetching results for: ${lastActiveView.query}`); // ADD LOG
          const res = await fetch(`${getApiBaseUrl()}/omdb?s=${encodeURIComponent(lastActiveView.query)}`);
          const data = await res.json();
          if (data.Response === 'True') {
            setSearchResults(data.Search);
            setCachedSearchResults(lastActiveView.query, data.Search);
            setNeedsMeasurementRefresh(true); // Trigger measurement refresh
            //console.log(`[LastActiveView Search Restore] Fetched and cached ${data.Search.length} results for: ${lastActiveView.query}`); // ADD LOG
          } else {
             //console.warn(`[LastActiveView Search Restore] API fetch failed or no results for: ${lastActiveView.query}. Error: ${data.Error}`); // ADD LOG
          }
        }
      } catch (e) {
        console.error('[LastActiveView Search Restore] Error restoring search:', e);
      } finally {
        setSearchLoading(false);
      }
    } else if (lastActiveView.type === 'grid' && lastActiveView.gridKey) {
      let gridRestored = false;
      setLoadingStarter(true);
      //console.log(`[LastActiveView Grid Restore] Attempting grid restore for key: ${lastActiveView.gridKey}`); // ADD LOG

      // FIRST: Try the primary cache (`starterGridCache` via `getGridCache`)
      try {
          //console.log(`[LastActiveView Grid Restore] Checking starterGridCache for key: ${lastActiveView.gridKey}`); // Typo fix here in original comment
          const cachedData = getGridCache(lastActiveView.gridKey); // getGridCache uses safeCacheStorage

          if (cachedData) {
              //console.log(`[LastActiveView Grid Restore] Found grid data in starterGridCache for key: ${lastActiveView.gridKey}`); // ADD LOG
              setSearchResults(cachedData);
              gridRestored = true;
              setNeedsMeasurementRefresh(true); // Trigger measurement refresh
          } else {
              // SECOND: If not cached, try re-fetching using IDs from grid definition (if available in `starterGrids` cache)
              //console.log(`[LastActiveView Grid Restore] Grid data not in starterGridCache for key: ${lastActiveView.gridKey}. Attempting fetch using stored IDs.`); // ADD LOG
              const cachedGrids = safeCacheStorage.getItem('starterGrids'); // Check if we have the list of grids
              if (cachedGrids) {
                  try {
                      const grids = JSON.parse(cachedGrids);
                      const grid = grids.find((g: GridData) => g.key === lastActiveView.gridKey);
                      if (grid && grid.imdbIDs && grid.imdbIDs.length > 0) {
                          //console.log('[LastActiveView Grid Restore] Re-fetching grid items using stored IDs for key:', grid.key);
                          const items = await fetchOmdbDataForGrid(grid.imdbIDs, grid.key); // This also sets cache via setGridCache
                          setSearchResults(items);
                          setNeedsMeasurementRefresh(true); // Trigger measurement refresh
                          gridRestored = true;
                          //console.log(`[LastActiveView Grid Restore] Fetched and cached ${items.length} items using stored IDs for key: ${grid.key}`); // ADD LOG
                      } else {
                          //console.warn(`[LastActiveView Grid Restore] Could not find grid definition or IDs in starterGrids cache for key: ${lastActiveView.gridKey}`); // ADD LOG
                      }
                  } catch (parseError) {
                       //console.error('[LastActiveView Grid Restore] Error parsing starterGrids cache:', parseError);
                  }
              } else {
                   //console.warn(`[LastActiveView Grid Restore] starterGrids cache is missing, cannot re-fetch using IDs.`); // ADD LOG
              }
          }
      } catch (e) {
          console.error('[LastActiveView Grid Restore] Error restoring grid from starterGridCache/fetch:', e);
      }

      // ---> REMOVE SECOND (SessionStorage Fallback) block <---
      /*
      if (!gridRestored) {
        // ... removed session storage fallback ...
      }
      */

      // THIRD: Try the API fallback (final resort if cache/ID fetch fails)
      if (!gridRestored) {
          try {
              //console.log('[LastActiveView Grid Restore] Attempting fallback grid fetch from API for key:', lastActiveView.gridKey); // ADD LOG
              const response = await fetch(`${getApiBaseUrl()}/grids/${lastActiveView.gridKey}`);
              if (response.ok) {
                  const grid = await response.json();
                  if (grid && grid.imdbIDs && grid.imdbIDs.length > 0) {
                      //console.log('[LastActiveView Grid Restore] Fetched fallback grid definition from API, now fetching items'); // ADD LOG
                      const items = await fetchOmdbDataForGrid(grid.imdbIDs, grid.key); // This also sets cache
                      setSearchResults(items);
                      setNeedsMeasurementRefresh(true); // Trigger measurement refresh
                      gridRestored = true;
                      //console.log(`[LastActiveView Grid Restore] Fetched and cached ${items.length} items via API fallback for key: ${grid.key}`); // ADD LOG
                  } else {
                      //console.warn(`[LastActiveView Grid Restore] API fallback for key ${lastActiveView.gridKey} returned invalid data.`); // ADD LOG
                  }
              } else {
                   //console.warn(`[LastActiveView Grid Restore] API fallback fetch failed for key ${lastActiveView.gridKey}. Status: ${response.status}`); // ADD LOG
              }
          } catch (fallbackError) {
              //console.error('[LastActiveView Grid Restore] Fallback grid fetch from API failed:', fallbackError);
          }
      }

      if (!gridRestored) {
           //console.error(`[LastActiveView Grid Restore] FAILED to restore grid for key: ${lastActiveView.gridKey} by any method.`); // ADD LOG
      }

      setLoadingStarter(false);
    } else if (lastActiveView.type === 'imdbList') {
      // Restore IMDb list
      let itemsRestored = false;
      //console.log('[LastActiveView IMDb List Restore] Attempting restore for URL/Title:', lastActiveView.url || lastActiveView.listTitle); // ADD LOG

      // FIRST: Try to use items stored directly in lastActiveView
      if (lastActiveView.items && Array.isArray(lastActiveView.items) && lastActiveView.items.length > 0) {
        //console.log(`[LastActiveView IMDb List Restore] Found ${lastActiveView.items.length} items directly in lastActiveView object.`); // ADD LOG
        setSearchResults(lastActiveView.items);
        setNeedsMeasurementRefresh(true); // Trigger measurement refresh
        itemsRestored = true;
        // console.log('[LastActiveView] Restored IMDb list items from lastActiveView');
      } else {
        //console.log('[LastActiveView IMDb List Restore] No items found directly in lastActiveView object.'); // ADD LOG
      }

      // ---> REMOVE SessionStorage fallback block <---
      /*
      else {
        // Try to get items from sessionStorage as fallback
        // ... removed session storage fallback ...
      }
      */
      
      // SECOND: If items weren't stored directly, try re-fetching using stored IDs
      if (!itemsRestored) {
          if (lastActiveView.imdbIds && Array.isArray(lastActiveView.imdbIds) && lastActiveView.imdbIds.length > 0) {
               //console.log(`[LastActiveView IMDb List Restore] Re-fetching ${lastActiveView.imdbIds.length} items using stored IDs.`); // ADD LOG
              setImportLoading(true); // Show loading indicator for the fetch
              try {
                  const fetchedItems = await fetchOmdbDataForIds(lastActiveView.imdbIds);
                  if (fetchedItems.length > 0) {
                       setSearchResults(fetchedItems);
                       setNeedsMeasurementRefresh(true);
                       itemsRestored = true;
                       //console.log(`[LastActiveView IMDb List Restore] Successfully re-fetched ${fetchedItems.length} items.`); // ADD LOG
                       // OPTIONAL: Could update lastActiveView here to include items, but risks bloat. Skipping for now.
                       /*
                       const updatedView = { ...lastActiveView, items: fetchedItems };
                       setLastActiveView(updatedView); // Triggers save effect
                       */
                  } else {
                       //console.error('[LastActiveView IMDb List Restore] Re-fetch using stored IDs returned no items.');
                  }
              } catch (fetchError) {
                  //console.error('[LastActiveView IMDb List Restore] Error re-fetching items:', fetchError);
                  setImportError('Failed to restore list items. Please try loading the list again.'); // Show user error
              } finally {
                  setImportLoading(false);
              }
          } else {
               //console.warn('[LastActiveView IMDb List Restore] Cannot re-fetch items: No items in lastActiveView and no imdbIds found.'); // ADD LOG
          }
      }

      // Set list title if available (keep this part, restoring title is fine)
      if (lastActiveView.listTitle) {
        setListTitle(lastActiveView.listTitle);
        //console.log(`[LastActiveView IMDb List Restore] Restored list title: ${lastActiveView.listTitle}`); // ADD LOG
      } else {
         //console.log(`[LastActiveView IMDb List Restore] No list title found in lastActiveView object.`); // ADD LOG
         // Removing fallback check for title from session storage for consistency
         /*
         try {
           const sessionTitle = sessionStorage.getItem('imdbListTitle');
           // ...
         } catch (e) {}
         */
      }

      if (!itemsRestored) {
        //console.error('[LastActiveView IMDb List Restore] FAILED to restore IMDb list items by any method.'); // ADD LOG
      }
    } else if (lastActiveView.type === 'apiSlug' && lastActiveView.items) {
        // Logic for restoring from API slug data already present in lastActiveView
        //console.log(`[LastActiveView Slug Restore] Restoring ${lastActiveView.items.length} items for slug: ${lastActiveView.slug}`); // ADD LOG
        setSearchResults(lastActiveView.items);
        setNeedsMeasurementRefresh(true);
        if (lastActiveView.listTitle) {
          setListTitle(lastActiveView.listTitle);
          //console.log(`[LastActiveView Slug Restore] Restored list title: ${lastActiveView.listTitle}`); // ADD LOG
        }
    } else {
      //console.log('[LastActiveView] Unknown or incomplete view type, or missing necessary data:', lastActiveView.type, lastActiveView); // Updated log message
    }
  };

  // New effect to handle measurement refresh after searchResults are populated
  useEffect(() => {
    if (needsMeasurementRefresh && searchResults.length > 0 && imdbSearchRef.current) {
      //console.log('[MeasurementRefresh] Triggering remeasurement for ImdbSearch with', searchResults.length, 'results');
      
      // Detect iOS specifically for more aggressive handling
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      //console.log('[MeasurementRefresh] iOS detection:', isIOS);
      
      // Force a re-measurement by triggering any method in the ImdbSearch ref that would cause remeasurement
      if (imdbSearchRef.current.remeasureContainerSize) {
        // If ImdbSearch exposes a direct remeasure method
        imdbSearchRef.current.remeasureContainerSize();
        //console.log('[MeasurementRefresh] Called explicit remeasureContainerSize method');
      } else if (imdbSearchRef.current.forceUpdate) {
        // Or use forceUpdate if available
        imdbSearchRef.current.forceUpdate();
        //console.log('[MeasurementRefresh] Called forceUpdate method');
      } else {
        // Fallback: simulate a window resize to trigger measurements
        //console.log('[MeasurementRefresh] No direct method available, dispatching resize event');
        window.dispatchEvent(new Event('resize'));
      }
      
      // Clear the flag
      setNeedsMeasurementRefresh(false);
      
      // Set a sequence of timers for delayed remeasurement - crucial for iOS
      if (isIOS || isTapInterface) {
        const timers = [200, 500, 1000, 2000];
        timers.forEach(delay => {
          setTimeout(() => {
            // console.log(`[MeasurementRefresh] Performing delayed follow-up measurement after ${delay}ms`);
            if (imdbSearchRef.current && imdbSearchRef.current.remeasureContainerSize) {
              imdbSearchRef.current.remeasureContainerSize();
            } else {
              window.dispatchEvent(new Event('resize'));
            }
          }, delay);
        });
      } else {
        // Just one follow-up for non-iOS/desktop
        setTimeout(() => {
          // console.log('[MeasurementRefresh] Performing delayed secondary remeasurement');
          window.dispatchEvent(new Event('resize'));
          if (!initialMeasurementDone.current) {
            initialMeasurementDone.current = true;
          }
        }, 1000); // 1 second delay to ensure layout has completed
      }
    }
  }, [needsMeasurementRefresh, searchResults, imdbSearchRef, isTapInterface]);

  // Add effect for additional emergency re-measurement on mobile
  useEffect(() => {
    // Only run this on mobile interfaces
    if (!isTapInterface) return;
    
    const hasResults = searchResults.length > 0;
    
    if (hasResults && imdbSearchRef.current) {
      const timings = [1500, 3000, 5000]; // Try at 1.5s, 3s, and 5s after mount
      
      const timeouts = timings.map(ms => 
        setTimeout(() => {
          // console.log(`[MobileRender] Emergency measurement check at ${ms}ms`);
          if (imdbSearchRef.current && imdbSearchRef.current.remeasureContainerSize) {
            imdbSearchRef.current.remeasureContainerSize();
          } else {
            window.dispatchEvent(new Event('resize'));
          }
        }, ms)
      );
      
      return () => {
        timeouts.forEach(t => clearTimeout(t));
      };
    }
  }, [searchResults, isTapInterface]);

  // Helper to load from local storage (called initially or as fallback)
  const loadFromLocalStorage = () => {
    const namespace = getCurrentStorageNamespace();
    const key = `tiers_${namespace}`;
    const saved = safeCacheStorage.getItem(key);
    if (saved) {
      try {
        const parsedTiers = JSON.parse(saved);
        const validatedTiers = parsedTiers.map((tier: any) => ({
          ...tier,
          items: tier.items.map((item: any) => ({ ...item, fromTier: true }))
        }));
        setTiers(validatedTiers);
      } catch (error) {
        console.error("Error parsing tiers from localStorage:", error);
        setTiers(DEFAULT_TIERS);
      }
    } else {
      setTiers(DEFAULT_TIERS);
    }
  };

  const [activeDrag, setActiveDrag] = useState<any>(null);
  const [loadingStarter, setLoadingStarter] = useState(false);
  const [cardSize, setCardSize] = useState(() => {
    const saved = safeCacheStorage.getItem('cardSize');
    return saved || 'medium';
  });

  // Define a custom "instant" drop animation with 0 duration
  const instantDropAnimation = {
    duration: 0,
    easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
  };

  // Determine the effective interaction mode
  const effectiveTapInterface = isTapInterface || useTapInterfaceOverride;

  // ---> Add ref to track poster fix attempts <---
  const posterFixAttemptedIds = useRef(new Set<string>());

  // Fix missing/invalid posters after loading from localStorage
  useEffect(() => {
    // Find all items with missing/invalid posters that haven't been attempted yet
    const itemsToFix: { tierIdx: number; itemIdx: number; imdbID: string }[] = [];
    tiers.forEach((tier, tierIdx) => {
      tier.items.forEach((item, itemIdx) => {
        // Only attempt fix if Poster is invalid AND ID hasn't been attempted in this session
        if ((!item.Poster || item.Poster === 'N/A' || item.Poster === '') && !posterFixAttemptedIds.current.has(item.imdbID)) {
          itemsToFix.push({ tierIdx, itemIdx, imdbID: item.imdbID });
        }
      });
    });
    if (itemsToFix.length === 0) return;

    // console.log(`[PosterFix] Attempting to fix posters for ${itemsToFix.length} items.`);

    let cancelled = false;
    (async () => {
      // Add IDs to attempted set *before* fetching
      itemsToFix.forEach(item => posterFixAttemptedIds.current.add(item.imdbID));

      // Fetch OMDb data for all missing posters
      const fetches = await Promise.all(
        itemsToFix.map(async ({ imdbID }) => {
          try {
            const res = await fetch(`${getApiBaseUrl()}/omdb?i=${imdbID}`);
            // Basic check for ok status, still might return 'N/A' poster
            if (!res.ok) return null;
            const data = await res.json();
            // Log the poster URL received for this ID
            // console.log(`[PosterFix Effect] OMDb result for ${imdbID}: Poster=${data?.Poster}`);
            return data;
          } catch {
            return null;
          }
        })
      );
      if (cancelled) return;
      // Update tiers with new poster data and mark as attempted
      setTiers(prevTiers => {
        const newTiers = prevTiers.map((tier, tierIdx) => {
          const newItems = tier.items.map((item, itemIdx) => {
            const fixInfo = itemsToFix.find(f => f.tierIdx === tierIdx && f.itemIdx === itemIdx);
            if (fixInfo) {
              const fetched = fetches[itemsToFix.indexOf(fixInfo)];
              // Merge fetched data if successful, but always mark attempt
              const updatedItem = {
                ...item,
                // Only update Poster if fetched is valid
                Poster: (fetched && fetched.Response === 'True' && fetched.Poster && fetched.Poster !== 'N/A') 
                  ? fetched.Poster 
                  : 'N/A' // Explicitly set to N/A if fetch fails or poster is invalid
              };
              return updatedItem;
            }
            return item;
          });
          return { ...tier, items: newItems };
        });
        // console.log("[PosterFix] Updated tiers state after fix attempt."); // Optional: Add logging
        return newTiers;
      });
    })();
    return () => { cancelled = true; };
  }, [tiers]); // Re-run if tiers change, but only process new/unattempted items

  // All poster IDs in all tiers (for uniqueness)
  const allTierIds = tiers.flatMap((tier: { items: any[] }) => tier.items.map((item: any) => item.imdbID));

  // Custom setActiveDrag handler to ensure poster data is valid
  const handleSetActiveDrag = useCallback((info: any) => {
    // Handle both old and new parameter formats
    if (!info) {
      setActiveDrag(null);
      return;
    }

    // If info is not an object with an 'item' property, treat it as the item itself (backward compatibility)
    const itemInfo = info.item !== undefined ? info : { item: info };
    const { item, tierIdx, itemIdx } = itemInfo;

    if (!item) {
      setActiveDrag(null);
      return;
    }

    // console.log('Dragging item:', item.imdbID, item.Title);
    // console.log('fromTier marker:', item.fromTier ? 'YES' : 'NO');

    // If tierIdx and itemIdx are provided, use the exact item from tiers
    if (typeof tierIdx === 'number' && typeof itemIdx === 'number' && tiers[tierIdx] && tiers[tierIdx].items[itemIdx]) {
      // console.log('Using exact item from tier', tierIdx, 'index', itemIdx);
      const tierItem = tiers[tierIdx].items[itemIdx];
      // Ensure it has the fromTier marker
      if (!tierItem.fromTier) {
        tierItem.fromTier = true;
      }
      setActiveDrag(tierItem);
      return;
    }

    // Check if the item has the fromTier marker
    if (item.fromTier) {
      // console.log('Item has fromTier marker, using it directly');
      setActiveDrag(item);
      return;
    }

    // If we get here, the item is from search results and not in any tier
    // console.log('Item is from search results, not in any tier');
    setActiveDrag(item);
  }, [tiers]); // Dependency: tiers (to access exact item)

  // Handle tap on poster in search results
  const handleSearchItemTap = useCallback((item: any, position: { x: number, y: number }) => {
    if (!effectiveTapInterface || tierSelectorOpen) return; // Ignore tap if selector is open or not in tap mode
    
    // console.log('[App Tap Flow] Search item tapped:', item.Title, item.imdbID);
    // console.log('[App Tap Flow] Current selectedItem before set:', selectedItem?.imdbID);
    
    // Clear all state at once
    setSelectedItem(item);
    setTapPosition(position);
    setSourceTierIdx(null);
    setSourceItemIdx(null);
    
    // Open after state is set
    // console.log('[App] Opening tier selector for search item');
    setTierSelectorOpen(true);
  }, [effectiveTapInterface, tierSelectorOpen, selectedItem]); // Dependencies

  // Handle tap on poster in tier
  const handleTierItemTap = useCallback((item: any, tierIdx: number, itemIdx: number, position: { x: number, y: number }) => {
    if (!effectiveTapInterface || tierSelectorOpen || positionSelectorOpen) return; // Ignore tap if selector is open or not in tap mode
    
    // console.log('[App Tap Flow] Tier item tapped:', item.Title, item.imdbID, 'in tier', tierIdx, 'at index', itemIdx);
    // console.log('[App Tap Flow] Current selectedItem before set:', selectedItem?.imdbID);
    
    // Clear all state at once
    setSelectedItem(item);
    setTapPosition(position);
    setSourceTierIdx(tierIdx);
    setSourceItemIdx(itemIdx);
    
    // Open after state is set
    // console.log('[App] Opening tier selector for tier item');
    setTierSelectorOpen(true);
  }, [effectiveTapInterface, tierSelectorOpen, selectedItem, positionSelectorOpen]); // Dependencies

  // Handle selection of tier from popup
  const handleTierSelect = (destTierIdx: number) => {
    if (!selectedItem) return;
    
    // console.log('[App] Tier selected for item:', selectedItem.Title, 'to tier', destTierIdx);
    
    // Case 1: Move from search to tier
    if (sourceTierIdx === null) {
      // Check if already in any tier
      if (allTierIds.includes(selectedItem.imdbID)) {
        // console.log('[App] Item already in a tier, not adding');
        handleTierSelectorClose();
        return;
      }
      
      // Add to destination tier
      const newTiers = tiers.map((tier: any, idx: number) =>
        idx === destTierIdx
          ? { ...tier, items: [...tier.items, { ...selectedItem, fromTier: true }] }
          : tier
      );
      setTiers(newTiers);
    }
    // Case 2: Move from one tier to another
    else if (sourceTierIdx !== null && sourceItemIdx !== null) {
      // If moving to same tier, show position selector instead
      if (sourceTierIdx === destTierIdx) {
        // console.log('[App] Same tier selected, showing position selector');
        setTierSelectorOpen(false);
        setPositionSelectorOpen(true);
        return;
      }
      
      // Get source tier and item
      const sourceTier = tiers[sourceTierIdx];
      const item = sourceTier.items[sourceItemIdx];
      
      // Check if already in destination tier
      const destTier = tiers[destTierIdx];
      if (destTier.items.some((i: any) => i.imdbID === item.imdbID)) {
        // console.log('[App] Item already in destination tier, not moving');
        handleTierSelectorClose();
        return;
      }
      
      // Remove from source tier and add to destination
      const newSourceItems = sourceTier.items.filter((_: any, i: number) => i !== sourceItemIdx);
      const newDestItems = [...destTier.items, { ...item, fromTier: true }];
      
      const newTiers = tiers.map((t: any, idx: number) => {
        if (idx === sourceTierIdx) return { ...t, items: newSourceItems };
        if (idx === destTierIdx) return { ...t, items: newDestItems };
        return t;
      });
      
      setTiers(newTiers);
    }
    
    // Close the selector after updating tiers
    handleTierSelectorClose();
  };

  // Handle reordering in the current tier
  const handleReorderInTier = () => {
    if (sourceTierIdx === null || sourceItemIdx === null) return;
    
    // console.log('[App] Reorder in current tier selected');
    setTierSelectorOpen(false);
    setPositionSelectorOpen(true);
  };
  
  // Handle position selection
  const handlePositionSelect = (newPosition: 'start' | 'end' | number) => {
    if (sourceTierIdx === null || sourceItemIdx === null || !selectedItem) return;
    
    // console.log('[App] Position selected:', newPosition);
    
    const tierItems = [...tiers[sourceTierIdx].items];
    const item = tierItems[sourceItemIdx];
    
    // Remove item from its current position
    tierItems.splice(sourceItemIdx, 1);
    
    // Add item at the new position
    let insertIdx: number;
    if (newPosition === 'start') {
      insertIdx = 0;
      // console.log('[App] Moving item to start');
    } else if (newPosition === 'end') {
      insertIdx = tierItems.length;
      // console.log('[App] Moving item to end');
    } else {
      // The position passed is already adjusted in the PositionSelector component
      insertIdx = newPosition;
      
      // Check if the insertion index needs adjustment
      // If the chosen position was after the removed item, we need to decrement
      // because removing the original item shifted positions
      if (insertIdx >= sourceItemIdx) {
        // console.log('[App] Chosen position was after original, adjusting index');
        insertIdx = Math.min(insertIdx, tierItems.length);
      }
      
      // console.log('[App] Moving item to position:', insertIdx, 'out of', tierItems.length);
    }
    
    // Insert the item at the calculated position
    tierItems.splice(insertIdx, 0, item);
    // console.log('[App] New tier items order:', tierItems.map(i => i.imdbID));
    
    // Update the tier with new item order
    const newTiers = tiers.map((tier, idx) => {
      if (idx === sourceTierIdx) {
        return { ...tier, items: tierItems };
      }
      return tier;
    });
    
    setTiers(newTiers);
    handlePositionSelectorClose();
  };

  // Handle position selector close
  const handlePositionSelectorClose = () => {
    // console.log('[App] Position selector close requested');
    setPositionSelectorOpen(false);
    // Reset state
    setSelectedItem(null);
    setSourceTierIdx(null);
    setSourceItemIdx(null);
  };

  // Handle removal from tier
  const handleRemoveFromTier = () => {
    if (sourceTierIdx === null || sourceItemIdx === null) return;
    
    // console.log('[App] Removing item from tier', sourceTierIdx);
    
    // Remove from tier
    const newTiers = tiers.map((tier: any, idx: number) =>
      idx === sourceTierIdx
        ? { ...tier, items: tier.items.filter((_: any, i: number) => i !== sourceItemIdx) }
        : tier
    );
    
    setTiers(newTiers);
    handleTierSelectorClose();
  };

  // Handle drag end
  const handleDragEnd = (event: any) => {
    // --> Add check for read-only view <--
    if (isReadOnlyView) {
      setActiveDrag(null); // Ensure drag overlay clears
      return; // Do not process drag events in read-only mode
    }

    // Skip drag operations on mobile (using tap interface instead)
    if (effectiveTapInterface) {
      setActiveDrag(null); // Use null directly
      return;
    }
    
    const { active, over } = event;
    // --> Add log here <--
    // console.log('[handleDragEnd Start] active:', active?.id, 'over:', over?.id, 'over.data:', over?.data?.current);

    // If no drop target or drag cancelled implicitly
    if (!over) {
      // --> Add log here <--
      // console.log('[handleDragEnd] No drop target (over is null/undefined). Bailing out.'); 
      setActiveDrag(null); // Use null directly
      return;
    }

    // Extract the actual ID, removing potential prefixes
    const activeId = typeof active.id === 'string' && active.id.startsWith('tier-item-') 
      ? active.id.replace('tier-item-', '') 
      : active.id;

    // Find source tier and item index
    let sourceTierIdx = -1, sourceIdx = -1;
    tiers.forEach((tier: { items: any[] }, tIdx: number) => {
      const idx = tier.items.findIndex((i: any) => i.imdbID === activeId); // Use extracted ID
      if (idx !== -1) {
        sourceTierIdx = tIdx;
        sourceIdx = idx;
      }
    });

    // Find destination tier index or check for trash
    let destTierIdx = -1;
    let droppedOnTrash = false;
    let destIndex: number | undefined = undefined; // Index within the destination tier

    if (over.id === 'trash') {
        droppedOnTrash = true;
    } else if (over.data?.current?.tierIdx !== undefined) {
      destTierIdx = over.data.current.tierIdx;
      // If dropped onto an item, get its index
      if (over.data.current.itemIdx !== undefined) {
        destIndex = over.data.current.itemIdx;
      }
    } else if (typeof over.id === 'string' && over.id.startsWith('tier-')) {
      destTierIdx = parseInt(over.id.replace('tier-', ''));
      // Dropped directly onto tier background - implies appending
      destIndex = undefined; 
    } else if (typeof over.id === 'string' && over.id.startsWith('tier-item-')) {
      // If dropped on another item, extract both item ID and find its tier and index
      const targetItemId = over.id.replace('tier-item-', '');
      // console.log('[handleDragEnd] Dropped on item with ID:', targetItemId);
      
      // Locate the item in the tiers
      tiers.forEach((tier: { items: any[] }, tIdx: number) => {
        const idx = tier.items.findIndex((i: any) => i.imdbID === targetItemId);
        if (idx !== -1) {
          destTierIdx = tIdx;
          destIndex = idx;
        }
      });
    }
    
    // console.log(`[handleDragEnd] Identified source: tier=${sourceTierIdx}, idx=${sourceIdx}, dest: tier=${destTierIdx}, idx=${destIndex}`);

    // Perform state updates based on drop target
    if (droppedOnTrash && sourceTierIdx !== -1) {
        // Remove from source tier
        const newTiers = tiers.map((tier: { items: any[] }, idx: number) =>
            idx === sourceTierIdx
            ? { ...tier, items: tier.items.filter((_: any, i: number) => i !== sourceIdx) }
            : tier
        );
        // console.log('[handleDragEnd] Deleting item. New Tiers:', JSON.stringify(newTiers.map(t => ({ name: t.name, items: t.items.map(i => i.imdbID) }))));
        setTiers(newTiers);
    } else if (destTierIdx !== -1) {
        // --> Add logging here <--
        // console.log(`[handleDragEnd Decision] sourceTierIdx: ${sourceTierIdx}, destTierIdx: ${destTierIdx}, sourceIdx: ${sourceIdx}, destIndex: ${destIndex}, activeId: ${activeId}, over.id: ${over.id}`);

        // Drag from search (copy)
        if (sourceTierIdx === -1) {
            const item = searchResults.find((i: any) => i.imdbID === activeId);
            if (item && !allTierIds.includes(item.imdbID)) {
                const newTiers = tiers.map((tier: { items: any[] }, idx: number) => {
                    if (idx === destTierIdx) {
                        const newItem = { ...item, fromTier: true };
                        const newDestItems = [...tier.items];
                        if (typeof destIndex === 'number') {
                            // Insert at specific index
                            newDestItems.splice(destIndex, 0, newItem);
                        } else {
                            // Append to the end
                            newDestItems.push(newItem);
                        }
                        return { ...tier, items: newDestItems };
                    }
                    return tier;
                });
                // console.log('[handleDragEnd] Adding from search. New Tiers:', JSON.stringify(newTiers.map(t => ({ name: t.name, items: t.items.map(i => i.imdbID) }))));
                setTiers(newTiers);
            }
        }
        // Move or reorder within/between tiers
        else if (sourceTierIdx !== -1) {
            if (sourceTierIdx === destTierIdx) {
                // Reorder within tier (existing logic is correct)
                const tier = tiers[sourceTierIdx];
                const oldIndex = sourceIdx;
                const newIndex = destIndex !== undefined ? destIndex : tier.items.length; // Use destIndex calculated earlier, or append to the end
                
                // --> Add logging here <--
                // console.log(`[handleDragEnd Reorder] Attempting reorder in tier ${sourceTierIdx}. Item ID: ${activeId}. oldIndex: ${oldIndex}, newIndex: ${newIndex}`);

                // Ensure we don't try to splice at the same index
                if (oldIndex !== newIndex) {
                    const newItems = [...tier.items];
                    const [moved] = newItems.splice(oldIndex, 1);
                    
                    // If the destination is after the source, we need to account for the removed item
                    const adjustedNewIndex = newIndex > oldIndex ? newIndex - 1 : newIndex;
                    newItems.splice(adjustedNewIndex, 0, moved);
                    
                    const newTiers = tiers.map((t: { items: any[] }, idx: number) => idx === sourceTierIdx ? { ...t, items: newItems } : t);
                    // console.log('[handleDragEnd] Reordering in tier. New Tiers:', JSON.stringify(newTiers.map(t => ({ name: t.name, items: t.items.map(i => i.imdbID) }))));
                    setTiers(newTiers);
                }
            } else {
                // Move between tiers
                const sourceTier = tiers[sourceTierIdx];
                const destTier = tiers[destTierIdx];
                const item = sourceTier.items[sourceIdx];
                // Only move if not already in dest tier
                if (!destTier.items.some((i: any) => i.imdbID === item.imdbID)) {
                    const newSourceItems = sourceTier.items.filter((_: any, i: number) => i !== sourceIdx);
                    const movedItem = { ...item, fromTier: true }; // Ensure marker is present
                    const newDestItems = [...destTier.items];

                    if (typeof destIndex === 'number') {
                        // Insert at specific index
                        newDestItems.splice(destIndex, 0, movedItem);
                    } else {
                        // Append to the end
                        newDestItems.push(movedItem);
                    }

                    const newTiers = tiers.map((t: { items: any[] }, idx: number) => {
                        if (idx === sourceTierIdx) return { ...t, items: newSourceItems };
                        if (idx === destTierIdx) return { ...t, items: newDestItems };
                        return t;
                    });
                    // console.log('[handleDragEnd] Moving between tiers. New Tiers:', JSON.stringify(newTiers.map(t => ({ name: t.name, items: t.items.map(i => i.imdbID) }))));
                    setTiers(newTiers);
                }
            }
        }
    }

    // Reset active drag state AFTER all potential state updates
    setActiveDrag(null); // Use null directly
  };

  // Fetch OMDb data for a list of IMDb IDs using the backend proxy
  const fetchOmdbDataForGrid = async (imdbIDs: string[], gridKey: string) => {
    setLoadingStarter(true);
    try {
      // Check cache first
      const cachedData = gridKey ? getGridCache(gridKey) : null;
      if (cachedData) {
        // console.log(`[Grid Cache] Using cached data for grid: ${gridKey}`);
        return cachedData;
      }

      // console.log(`[Grid Cache] Fetching fresh data for grid: ${gridKey}`);
      const results = await Promise.all(
        imdbIDs.map(async (id) => {
          const res = await fetch(`${getApiBaseUrl()}/omdb?i=${id}`);
          // ---> FIX: Await JSON parsing BEFORE logging/returning <---
          const data = await res.json(); 
          // Log the poster URL received for this ID
          //console.log(`[fetchOmdbDataForGrid] OMDb result for ${id}: Poster=${data?.Poster}`);
          return data; // Return the parsed data
          // ---> END FIX <---
        })
      );
      
      const filteredResults = results.filter((item) => item && item.Response !== 'False');
      
      // Cache the results if we have a valid grid key
      if (gridKey) {
        setGridCache(gridKey, filteredResults);
      }
      
      return filteredResults;
    } finally {
      setLoadingStarter(false);
    }
  };

  // Handle loading a starter grid (pre-defined IMDb IDs)
  const handleSelectStarterGrid = async (grid: GridData) => {
    if (!grid) return;

    // console.log('[StarterGrid] Selected grid:', grid.key);
    setLoadingStarter(true);
    setListTitle(''); // Clear list title when loading a grid
    setSearchQuery(''); // Clear search query
    setImdbListUrl(''); // Clear import URL
    setUserHasSorted(false); // Reset sort
    setSearchError(''); // Clear errors
    setImportError('');

    // Reset internal query in ImdbSearch component to prevent "no results" message lingering
    if (imdbSearchRef.current && imdbSearchRef.current.setQuery) {
      imdbSearchRef.current.setQuery('');
    }

    let loadedItems: any[] = []; // Variable to hold loaded items

    // First check if we have this grid cached locally
    try {
      const cached = getGridCache(grid.key);
      if (cached) {
        setSearchResults(cached);
        loadedItems = cached; // Store cached items
        setLoadingStarter(false);
        // --> ADD setLastActiveView HERE for cached load <--
        setLastActiveView({
          type: 'grid',
          gridKey: grid.key,
          items: cached // Include items for potentially faster restore
        });
        setNeedsMeasurementRefresh(true); // Trigger measurement refresh
        return;
      }
    } catch (e) {
      console.error('[StarterGrid] Error checking cache:', e);
      // Continue to fetch if cache check fails
    }

    // If not cached, fetch the grid data
    try {
      const items = await fetchOmdbDataForGrid(grid.imdbIDs, grid.key);
      setSearchResults(items);
      loadedItems = items; // Store fetched items
      setLoadingStarter(false);

      // Cache the fetched data (already done inside fetchOmdbDataForGrid if gridKey exists)
      // setGridCache(grid.key, items); 

      // --> ADD setLastActiveView HERE for fetched load <--
      setLastActiveView({
        type: 'grid',
        gridKey: grid.key,
        items: items // Include items for potentially faster restore
      });
      setNeedsMeasurementRefresh(true); // Trigger measurement refresh

    } catch (e) {
      console.error('[StarterGrid] Error fetching grid data:', e);
      setSearchError('Failed to load starter grid data.'); // Use searchError for feedback
      setLoadingStarter(false);
    }
  };

  // Save tiers to localStorage whenever they change, *unless* a shared list was just loaded
  useEffect(() => {
    if (!loadingSharedList && !isSharedListLoaded.current) {
      // Only save if not loading and if the initial load wasn't from a share link
      // console.log('[LocalStorage Save] Saving tiers to localStorage.');
      const namespace = getCurrentStorageNamespace();
      const key = `tiers_${namespace}`;
      safeCacheStorage.setItem(key, JSON.stringify(tiers));
    } else if (isSharedListLoaded.current) {
      // If a shared list was loaded, reset the flag after the first potential save attempt
      // This allows subsequent modifications to the loaded shared list to be saved.
      // console.log('[LocalStorage Save] Shared list loaded, skipping initial save, enabling future saves.');
      isSharedListLoaded.current = false;
    }
  }, [tiers, loadingSharedList, window.location.pathname]); // Add pathname dependency

  // Save card size to localStorage whenever it changes
  useEffect(() => {
    safeCacheStorage.setItem('cardSize', cardSize);
  }, [cardSize]);

  // Save tap interface override preference to localStorage
  useEffect(() => {
    safeCacheStorage.setItem('useTapInterfaceOverride', String(useTapInterfaceOverride));
  }, [useTapInterfaceOverride]);

  // Filter and sort search results
  const filteredResults = searchResults
    .filter(item =>
      item.Title?.toLowerCase().includes(filterText.toLowerCase())
    )
    .sort((a, b) => {
      // Only apply sort criteria if user has explicitly chosen one after the last search
      if (!userHasSorted) {
        return 0; // Maintain API order
      }

      const field = sortCriteria.field;
      const direction = sortCriteria.direction === 'asc' ? 1 : -1;

      let valA = a[field];
      let valB = b[field];

      // Handle potential 'N/A' or missing values for Year
      if (field === 'Year') {
        valA = valA && valA !== 'N/A' ? parseInt(valA.split('–')[0]) : 0; // Use start year for ranges
        valB = valB && valB !== 'N/A' ? parseInt(valB.split('–')[0]) : 0;
      }
      
      if (valA < valB) return -1 * direction;
      if (valA > valB) return 1 * direction;
      return 0;
    });

  // Effect to handle scroll listener for the back-to-top button
  useEffect(() => {
    const container = searchResultsContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      //// console.log('Scroll detected:', container.scrollTop);
      if (container.scrollTop > 100) { // Lowered threshold to 100px
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    container.addEventListener('scroll', handleScroll);
    
    // Initial check in case the container is already scrolled on load
    handleScroll(); 

    // Cleanup listener on component unmount
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [filteredResults]); // Re-run when filteredResults change

  // Copied from ImdbSearch component but modified for App.tsx
  const performSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearchLoading(true);
    setSearchError('');
    setListTitle(''); // Clear list title when searching
    
    // Reset internal query in ImdbSearch component to prevent "no results" message lingering
    if (imdbSearchRef.current && imdbSearchRef.current.setQuery) {
      imdbSearchRef.current.setQuery('');
    }
    
    // Check cache first
    const cached = getCachedItem(searchQuery);
    if (cached) {
      setSearchResults(cached);
      setSearchLoading(false);
      setUserHasSorted(false); // Reset sort override on new search
      
      // Save the last active search
      setLastActiveView({
        type: 'search',
        query: searchQuery
      });
      return;
    }
    
    try {
      const res = await fetch(`${getApiBaseUrl()}/omdb?s=${encodeURIComponent(searchQuery)}`);
      const data = await res.json();
      if (data.Response === 'True') {
        setSearchResults(data.Search);
        setCachedSearchResults(searchQuery, data.Search);
        
        // Save the last active search
        setLastActiveView({
          type: 'search',
          query: searchQuery
        });
      } else {
        setSearchResults([]);
        setCachedSearchResults(searchQuery, []);
        setSearchError(data.Error || 'No results found.');
      }
    } catch (e) {
      setSearchError('Failed to fetch results.');
    }
    
    setSearchLoading(false);
    setUserHasSorted(false); // Reset sort override on new search
  };
  
  // Helper function to fetch OMDb details for a list of IDs
  const fetchOmdbDataForIds = useCallback(async (imdbIDs: string[]) => {
    if (!imdbIDs || imdbIDs.length === 0) return [];

    const allItems = [];
    const batchSize = 10; // Keep batching

    // console.log(`[fetchOmdbDataForIds] Fetching details for ${imdbIDs.length} items...`);
    setImportLoading(true); // Use importLoading state for feedback

    for (let i = 0; i < imdbIDs.length; i += batchSize) {
      const batch = imdbIDs.slice(i, i + batchSize);
      const promises = batch.map(async (imdbId) => {
        try {
          // Call the API endpoint which handles its own DB caching
          const res = await fetch(`${getApiBaseUrl()}/omdb?i=${imdbId}`);
          if (!res.ok) {
            // Log detailed error for non-OK responses
            console.error(`[OMDb Fetch] Failed for ${imdbId}: Status ${res.status} ${res.statusText}`);
            try {
              const errorBody = await res.text(); // Try to get error body
              console.error(`[OMDb Fetch] Error body for ${imdbId}: ${errorBody}`);
            } catch {
              // Ignore if error body cannot be read
            }
            return null;
          }
          const itemData = await res.json();
          // Log the poster URL received for this ID
          console.log(`[fetchOmdbDataForIds] OMDb result for ${imdbId}: Poster=${itemData?.Poster}`);
          if (itemData.Response === 'True') {
            return { ...itemData, imdbID: itemData.imdbID || imdbId }; // Ensure imdbID consistency
          } else {
            // Log when OMDb explicitly says 'False'
            console.warn(`[OMDb Fetch] Response 'False' for ${imdbId}. OMDb Error: ${itemData.Error}`);
            return null;
          }
        } catch (error) {
          // Log network or parsing errors
          console.error(`[OMDb Fetch] Network/Parsing Error fetching ${imdbId}:`, error);
          return null;
        }
      });

      const batchResults = await Promise.all(promises);
      allItems.push(...batchResults.filter(Boolean));
      // console.log(`[fetchOmdbDataForIds] Fetched batch ${Math.floor(i / batchSize) + 1}, total items now: ${allItems.length}`);
    }
    // console.log(`[fetchOmdbDataForIds] Finished fetching. Total items: ${allItems.length}`);
    setImportLoading(false); // Stop loading indicator
    return allItems;
  }, [setImportLoading]); // Dependency on setImportLoading

  // Handle IMDb list import
  const handleImportList = async () => {
    if (!imdbListUrl.trim()) return;
    const url = imdbListUrl; // Store before clearing

    // ... (URL validation) ...
    const urlPattern = /^https?:\/\/(?:www\.|m\.)?imdb\.com\/(?:list\/ls\d+|user\/[^/]+\/lists)\/?.*/;
    if (!urlPattern.test(url)) {
      setImportError('Invalid IMDb list URL. It should be in the format: https://www.imdb.com/list/ls... or https://m.imdb.com/list/ls...');
      return;
    }
    
    if (imdbSearchRef.current && imdbSearchRef.current.setQuery) {
      imdbSearchRef.current.setQuery('');
    }
    
    setImportLoading(true);
    setImportError('');
    setListTitle('');
    setSearchResults([]); // Clear previous results immediately
    
    try {
      // Normalize URL: convert m.imdb.com to www.imdb.com to match backend expectations
      let normalizedUrl = url;
      if (normalizedUrl.includes('m.imdb.com')) {
        normalizedUrl = normalizedUrl.replace('m.imdb.com', 'www.imdb.com');
        // console.log('[Import] Normalized mobile URL to:', normalizedUrl);
      }

      const res = await fetch(`${getApiBaseUrl()}/import-list?url=${encodeURIComponent(normalizedUrl)}`);
      // Always try to parse JSON, even for errors
      const data = await res.json(); 
      
      if (!res.ok) {
         // Throw error using message from parsed JSON if available
         throw new Error(data.message || data.error || `Error: ${res.status} ${res.statusText}`);
      }
      
      // Check for the new imdbIds property
      if (data.imdbIds && Array.isArray(data.imdbIds)) {
        const fetchedTitle = data.title || '';
        const fetchedSource = data.source;
        const fetchedIds = data.imdbIds;

        setListTitle(fetchedTitle);
        setImdbListUrl(''); // Clear input on success
        setUserHasSorted(false);

        // console.log(`[Import] Received ${fetchedIds.length} IDs (source: ${fetchedSource}). Fetching details...`);

        // Fetch full details for the IDs
        const fetchedItems = await fetchOmdbDataForIds(fetchedIds);
        
        setSearchResults(fetchedItems); 
        setNeedsMeasurementRefresh(true); // Trigger remeasure after setting results

        // Save the last active view
        const lastActiveData = {
          type: 'imdbList',
          url: url,
          listTitle: fetchedTitle,
          imdbIds: fetchedIds, // Store IDs
          items: fetchedItems // Store fetched items for faster restore
        };
        setLastActiveView(lastActiveData);
        
        try {
          sessionStorage.setItem('lastActiveView', JSON.stringify(lastActiveData));
          // console.log('[Import] Saved lastActiveView with fetched items to sessionStorage');
        } catch (storageError) {
          console.error('Failed to cache IMDb list items to session storage:', storageError);
        }

      } else {
        // Handle case where response is OK but doesn't contain expected data
        console.error('Import API response missing imdbIds:', data);
        throw new Error('Import successful, but received unexpected data format from server.');
      }
    } catch (e) {
      console.error('Import error:', e);
      setImportError(e instanceof Error ? e.message : 'Failed to import IMDb list.');
      // Don't clear existing search results on error, already cleared above
    } finally {
      // Ensure loading is always stopped, even if fetchOmdbDataForIds wasn't called
      setImportLoading(false); 
    }
  };

  // Search for IMDb lists by title
  const searchImdbLists = async (query: string) => {
    if (!query.trim() || query.trim().length < 3) return;
    
    setIsSearchingLists(true);
    setLastSearchTerm(query); // Store the search term
    setHasSearched(true); // Mark that a search has been performed
    
    try {
      // Use the search endpoint to find matching IMDb lists
      const res = await fetch(`${getApiBaseUrl()}/search-imdb-lists?q=${encodeURIComponent(query)}`);
      
      if (!res.ok) {
        console.error('Error searching IMDb lists:', res.statusText);
        setImdbListSearchResults([]);
        return;
      }
      
      const data = await res.json();
      
      if (data && Array.isArray(data)) {
        // Add client-side deduplication by title (case insensitive)
        const uniqueTitles = new Set();
        const uniqueResults = data.filter((list: {id: string, title: string, url: string}) => {
          const titleLower = list.title.toLowerCase();
          if (uniqueTitles.has(titleLower)) {
            return false;
          }
          uniqueTitles.add(titleLower);
          return true;
        });
        
        setImdbListSearchResults(uniqueResults);
      } else {
        setImdbListSearchResults([]);
      }
    } catch (e) {
      console.error('Error searching IMDb lists:', e);
      setImdbListSearchResults([]);
    } finally {
      setIsSearchingLists(false);
    }
  };
  
  // Handle selecting an IMDb list from search results
  const handleImdbListSelect = async (list: { id: string, title: string, url: string }) => {
    if (!list || !list.url) return;
    const listUrl = list.url; // Store URL

    setImdbListSearchResults([]); 
    setIsImportMode(false); 
    setHasSearched(false); 
    
    if (imdbSearchRef.current && imdbSearchRef.current.setQuery) {
      imdbSearchRef.current.setQuery('');
    }
    
    setImportLoading(true);
    setImportError('');
    setListTitle('');
    setSearchResults([]); // Clear previous results
    
    try {
      // Use the existing import mechanism but handle the new response format
      const res = await fetch(`${getApiBaseUrl()}/import-list?url=${encodeURIComponent(listUrl)}`);
      const data = await res.json(); // Parse JSON regardless of status

      if (!res.ok) {
        throw new Error(data.message || data.error || `Error: ${res.status} ${res.statusText}`);
      }

      if (data.imdbIds && Array.isArray(data.imdbIds)) {
        const fetchedTitle = data.title || list.title; // Use API title, fallback to search result title
        const fetchedSource = data.source;
        const fetchedIds = data.imdbIds;

        setListTitle(fetchedTitle);
        setImdbListUrl(''); // Clear input field
        setUserHasSorted(false);

        // console.log(`[ListSelect] Received ${fetchedIds.length} IDs (source: ${fetchedSource}). Fetching details...`);

        // Fetch full details
        const fetchedItems = await fetchOmdbDataForIds(fetchedIds);
        
        setSearchResults(fetchedItems);
        setNeedsMeasurementRefresh(true);

        // Save last active view
        const lastActiveData = {
          type: 'imdbList',
          url: listUrl,
          listTitle: fetchedTitle,
          imdbIds: fetchedIds,
          items: fetchedItems 
        };
        setLastActiveView(lastActiveData);

        try {
          sessionStorage.setItem('lastActiveView', JSON.stringify(lastActiveData));
          // console.log('[ListSelect] Saved lastActiveView with fetched items to sessionStorage');
        } catch (storageError) {
          console.error('Failed to cache IMDb list items to session storage:', storageError);
        }
      } else {
        console.error('ListSelect API response missing imdbIds:', data);
        throw new Error('Load successful, but received unexpected data format from server.');
      }
    } catch (e) {
      console.error('List selection load error:', e);
      setImportError(e instanceof Error ? e.message : 'Failed to load selected IMDb list.');
    } finally {
      setImportLoading(false);
    }
  };
  
  // Cache functions (copied from ImdbSearch component)
  const getCachedSearchResults = (query: string) => {
    const cache = safeCacheStorage.getItem('searchResultsCache');
    if (!cache) return null;
    try {
      const parsed = JSON.parse(cache);
      return parsed[query] || null;
    } catch {
      return null;
    }
  };

  const setCachedSearchResults = (query: string, results: any[]) => {
    let cache: Record<string, any[]> = {};
    try {
      cache = JSON.parse(safeCacheStorage.getItem('searchResultsCache') || '{}');
    } catch {}
    cache[query] = results;
    safeCacheStorage.setItem('searchResultsCache', JSON.stringify(cache));
  };

  // Handle sorting change
  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const [field, direction] = event.target.value.split('-');
    setSortCriteria({ field, direction });
    setUserHasSorted(true); // User has explicitly chosen a sort order
  };

  // Handle tier selector close
  const handleTierSelectorClose = () => {
    // console.log('[App Tap Flow] Tier selector close requested.');
    // console.log('[App Tap Flow] Current selectedItem before reset:', selectedItem?.imdbID);
    setTierSelectorOpen(false);
    // Reset state immediately
    setSelectedItem(null);
    setSourceTierIdx(null);
    setSourceItemIdx(null);
  };

  // Add state for About modal
  const [showAboutModal, setShowAboutModal] = useState(false);

  // Effect to lock body scroll when modal is open
  useEffect(() => {
    if (showAboutModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset'; // Reset to default
    }
    // Cleanup function to ensure scroll is reset if component unmounts while modal is open
    return () => {
      document.body.style.overflow = 'unset'; // Reset to default
    };
  }, [showAboutModal]);

  // Save last active view to localStorage under the correct namespace
  useEffect(() => {
    const namespace = getCurrentStorageNamespace();
    const key = `lastActiveView_${namespace}`;

    if (lastActiveView) {
       // ---> ADD Enhanced Logging <---
       console.log(`[Save State] Saving ${key}. Type: ${lastActiveView.type}. Details:`, {
           query: lastActiveView.query,
           gridKey: lastActiveView.gridKey,
           slug: lastActiveView.slug,
           url: lastActiveView.url,
           listTitle: lastActiveView.listTitle,
           // Check actual presence and type/length for items/ids
           hasItems: !!lastActiveView.items && Array.isArray(lastActiveView.items),
           itemCount: Array.isArray(lastActiveView.items) ? lastActiveView.items.length : undefined,
           hasIds: !!lastActiveView.imdbIds && Array.isArray(lastActiveView.imdbIds),
           idCount: Array.isArray(lastActiveView.imdbIds) ? lastActiveView.imdbIds.length : undefined
       });
       // ---> END Enhanced Logging <---

      try {
        safeCacheStorage.setItem(key, JSON.stringify(lastActiveView));
        
        // Also save directly to sessionStorage for more reliable persistence on mobile
        try {
          sessionStorage.setItem(key, JSON.stringify(lastActiveView));
          // console.log(`[LastActiveView] Saved ${key} to both storage types`);
        } catch (e) {
          console.error(`[LastActiveView] Failed to save ${key} to sessionStorage:`, e);
        }
      } catch (e) {
        //console.error(`[LastActiveView] Failed to save ${key} to localStorage:`, e);
        // If localStorage fails, try sessionStorage directly
        try {
          sessionStorage.setItem(key, JSON.stringify(lastActiveView));
        } catch (innerError) {
          //console.error(`[LastActiveView] All storage methods failed for ${key}:`, innerError);
        }
      }
    } else {
       //console.log(`[Save State] No lastActiveView to save for namespace ${namespace}. Removing key: ${key}`); // Log removal
      // Actually remove the key if lastActiveView is null/undefined
      safeCacheStorage.removeItem(key);
      sessionStorage.removeItem(key); // Also remove from session storage if clearing
    }
  // }, [lastActiveView]); // Original dependency array
  }, [lastActiveView, window.location.pathname]); // Add pathname dependency
  
  // Add special effect to re-save active view data on mobile before page unload
  useEffect(() => {
    if (!isTapInterface) return; // Only needed on mobile devices
    
    const handleBeforeUnload = () => {
      // console.log('[PageUnload] Saving lastActiveView and searchResults before mobile refresh/navigation');
      
      // If we have a lastActiveView, make sure it's saved
      if (lastActiveView) {
        try {
          // If the current view is a search, grid, or imdbList, save the current searchResults with it
          if (searchResults.length > 0 && 
             (lastActiveView.type === 'search' || lastActiveView.type === 'grid' || lastActiveView.type === 'imdbList')) {
            const enhancedView = {
              ...lastActiveView,
              items: searchResults // Always include the current items
            };
            sessionStorage.setItem('lastActiveView', JSON.stringify(enhancedView));
            localStorage.setItem('lastActiveView', JSON.stringify(enhancedView));
          } else {
            sessionStorage.setItem('lastActiveView', JSON.stringify(lastActiveView));
            localStorage.setItem('lastActiveView', JSON.stringify(lastActiveView));
          }
        } catch (e) {
          console.error('[PageUnload] Failed to save lastActiveView:', e);
        }
      }
      
      // Save current search results to sessionStorage directly
      if (searchResults.length > 0) {
        try {
          sessionStorage.setItem('emergencySearchResults', JSON.stringify(searchResults));
        } catch (e) {
          console.error('[PageUnload] Failed to save emergency search results:', e);
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [lastActiveView, searchResults, isTapInterface]);

  // ---> ADD HANDLER FOR CLEARING SEARCH/GRID RESULTS & STORAGE <---
  const handleClearSearchResultsAndStorage = () => {
    // console.log("[Clear List] Clearing search results, state, and storage...");
    setSearchResults([]);
    setFilterText('');
    setListTitle('');
    setSearchQuery('');
    setImdbListUrl('');
    setUserHasSorted(false);
    setSearchError(''); // Clear any search errors
    setImportError(''); // Clear any import errors
    setImdbListSearchResults([]); // Clear list search results

    // Clear storage related to the current displayed list
    safeCacheStorage.removeItem('searchResultsCache'); // General search cache
    safeCacheStorage.removeItem('starterGridCache');   // Grid cache
    sessionStorage.removeItem('lastGridKey');
    sessionStorage.removeItem('lastGridItems');
    sessionStorage.removeItem('imdbListItems');
    sessionStorage.removeItem('imdbListTitle');
    sessionStorage.removeItem('emergencySearchResults'); // Clear emergency save

    // Clear last active view for the current context
    const namespace = getCurrentStorageNamespace();
    const lastActiveKey = `lastActiveView_${namespace}`;
    safeCacheStorage.removeItem(lastActiveKey);
    sessionStorage.removeItem(lastActiveKey);
    setLastActiveView(null);

    // Optionally, reset scroll position of the search results container
    if (searchResultsContainerRef.current) {
      searchResultsContainerRef.current.scrollTop = 0;
    }
    // Close any open accordion sections maybe? Or leave them. Let's leave them for now.
    // setOpenSection(null); 
  };
  // ---> END HANDLER <---

  // Display loading or error state for shared lists
  if (loadingSharedList) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-200 flex items-center justify-center">
        <div className="text-xl font-semibold">Loading Tier List...</div>
      </div>
    );
  }
  if (sharedListError) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-200 flex flex-col items-center justify-center">
        <div className="text-xl font-semibold text-red-500 mb-4">Error Loading Shared List</div>
        <p className="text-gray-400 mb-6">{sharedListError}</p>
        <button 
          onClick={() => window.location.href = '/'} // Go back to home
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
        >
          Go to Home
        </button>
      </div>
    );
  }

  // ---> Determine metadata values <---
  const currentPath = window.location.pathname;
  const canonicalUrl = `https://tiermdb.com${currentPath === '/' ? '' : currentPath}`; // Assuming tiermdb.com as the domain

  let pageTitle = 'TierMDB - Movie & TV Tier List Creator';
  let metaDescription = 'Create and share movie and TV show tier lists with ease, powered by IMDb lists!';

  // Corrected regex: remove extra backslashes before / and - 
  const slugMatch = currentPath.match(/^\/([a-zA-Z0-9-]+-tier-list)$/);
  const isSlugView = slugMatch && slugMatch[1];

  if (isSlugView && listTitle) {
    pageTitle = `"${listTitle}" Tier List - TierMDB`;
    metaDescription = `Create and view the "${listTitle}" tier list on TierMDB. Drag and drop movies and TV shows into tiers.`;
  } else if (isReadOnlyView) {
    pageTitle = 'View Shared Tier List - TierMDB';
    metaDescription = 'View a shared movie and TV show tier list created on TierMDB.';
  }
  // ---> End metadata determination <---

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 flex flex-col">
      {/* ---> Add Helmet component <--- */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      {/* ---> End Helmet component <--- */}

      {/* Header */}
      <header className="bg-gray-800 text-white py-3 flex-shrink-0">
        {/* Main header container: flex, justify-between to push logo/text left and leave space right */}
        <div className="w-full max-w-[1920px] mx-auto px-4 flex items-center justify-between h-16">
          {/* Left container for logo and text */}
          <div className="flex items-center">
            {/* Logo: Added flex-shrink-0 to prevent shrinking */}
            <a href="/" aria-label="Home" className="flex-shrink-0">
              {/* Reverted to fixed height, removed w-auto */}
              <img 
                src="/tiermdb-logo-transparent.png" 
                alt="TierMDB Logo" 
                className="h-10 md:h-12 lg:h-14 mr-4" 
              />
            </a>
            {/* Header text: Increased margin to ml-6 for more spacing */}
            <h1 className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-semibold text-gray-300 text-right ml-6 min-w-0">
              Movie & TV Tier list creator, powered by IMDb lists!
            </h1>
          </div>
          {/* Reserved space on the right for future ad (implicitly created by justify-between) */}
        </div>
      </header>
      <main className="w-full max-w-[1920px] mx-auto px-4 py-6 flex flex-col flex-grow">
        {/* Add Edit Button for Read-Only View */}
        {isReadOnlyView && (
          <div className="mb-3 p-2 bg-yellow-900 bg-opacity-30 border border-yellow-500 rounded-lg flex flex-col sm:flex-row items-center justify-center gap-1.5 text-center sm:text-left">
            {/* Remove flex-grow from paragraph */}
            <p className="text-yellow-200 text-xs md:text-sm">
              You are viewing a shared list. Editing is disabled. Click the button to make your own editable copy.
            </p>
            <button
              onClick={handleEditSharedList}
              className="bg-yellow-600 hover:bg-yellow-700 text-black px-3 py-1 rounded text-xs font-semibold flex items-center gap-1 whitespace-nowrap flex-shrink-0"
            >
              <FaEdit size={12} className="mr-0.5"/> Edit this List
            </button>
          </div>
        )}

        <DndContext 
          collisionDetection={rectIntersection} 
          onDragEnd={handleDragEnd} 
          onDragCancel={() => setActiveDrag(null)}
          // Disable animations during active drag
          measuring={{
            droppable: {
              strategy: 'always'
            }
          }}
        >
          {/* Add a global no-animation style during drag operations */}
          {activeDrag && (
            <style>
              {`
                .sortable-items-container * {
                  transition: none !important;
                }
                
                /* Allow the drag overlay to be visible */
                [data-dnd-overlay] {
                  transition: none !important;
                  transform: translate3d(var(--translate-x, 0), var(--translate-y, 0), 0) !important;
                  z-index: 999 !important;
                  animation: none !important;
                }
              `}
            </style>
          )}
          
          {/* Two-column layout - Added relative positioning for toggle button */}
          <div className="flex flex-col md:flex-row gap-6 flex-grow relative">
            {/* Left column: Search and Starter Grids */}
            <div
              className={`
                transition-all duration-300 ease-in-out
                ${isLeftColumnCollapsed
                    ? 'md:w-0 md:opacity-0 md:invisible md:p-0 md:m-0 pointer-events-none' // Collapsed desktop: Hide and remove space
                    : 'md:w-5/12 lg:w-2/5 xl:w-1/3' // Expanded desktop: Original widths
                }
              `}
            >
              {/* Inner scrollable container - classes remain mostly the same */}
              <div
                ref={searchResultsContainerRef}
                className={[
                  "relative space-y-6 h-full", // Base styles, ensure height takes available space
                  isTapInterface
                    ? "" // No specific height/overflow styles on mobile, allow natural height and page scroll
                    : "overflow-y-auto md:max-h-[calc(100vh-120px)] scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 pr-6" // Desktop scroll/height + padding
                ].filter(Boolean).join(' ')}
              >
                {/* Starter Grid Accordion */}
                <div className="bg-gray-800 rounded-lg shadow-sm overflow-hidden">
                  <button
                    className="w-full text-left px-4 py-3 bg-gray-700 hover:bg-gray-600 focus:outline-none"
                    onClick={() => setOpenSection(openSection === 'starter' ? null : 'starter')}
                  >
                    <h2 className="text-lg font-bold text-white">Load Starter List</h2>
                  </button>
                  {openSection === 'starter' && (
                    <div className="p-4">
                      <StarterGridSelector onSelect={handleSelectStarterGrid} />
                      {loadingStarter && (
                        <div className="text-center text-blue-400 font-semibold mt-2">Loading starter grid...</div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* IMDb Lists Search / Import Accordion */}
                <div className="bg-gray-800 rounded-lg shadow-sm">
                  <button
                    className="w-full text-left px-4 py-3 bg-gray-700 hover:bg-gray-600 focus:outline-none"
                    onClick={() => setOpenSection(openSection === 'import' ? null : 'import')}
                  >
                    <h2 className="text-lg font-bold text-white">IMDb Lists Search / Import</h2>
                  </button>
                  {openSection === 'import' && (
                    <div className="p-4">
                      <h3 className="text-sm text-gray-300 mb-3">Search our database of imported <a href="https://m.imdb.com/user/ur23892615/lists/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">IMDb lists</a>, or input one you've created or want to create a tier list from.</h3>
                      
                      <div className="relative">
                        <input
                          className="border border-gray-600 rounded px-2 py-1 w-full text-sm bg-gray-700 text-white placeholder-gray-400 mb-2"
                          placeholder="Enter a search query or IMDb list URL..."
                          value={imdbListUrl}
                          onChange={(e) => {
                            setImdbListUrl(e.target.value);
                            // If input doesn't look like a URL, treat it as a search query
                            const isUrl = /^https?:\/\//i.test(e.target.value);
                            setIsImportMode(isUrl); // Set mode based on input type
                            
                            if (!isUrl && e.target.value.trim().length > 2) {
                              // Search for IMDb lists that match this query
                              searchImdbLists(e.target.value);
                            } else {
                              // Clear search results if it looks like a URL or input is too short
                              setImdbListSearchResults([]);
                              setHasSearched(false); // Reset search state
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              // If the input is a URL, import it
                              const isUrl = /^https?:\/\//i.test(imdbListUrl);
                              
                              // Only process if this isn't happening immediately after a tap on search results
                              // by checking if imdbListUrl actually has content
                              if (imdbListUrl.trim()) {
                                if (isUrl) {
                                  handleImportList();
                                } else if (imdbListSearchResults.length > 0) {
                                  // If we have search results, select the first one
                                  handleImdbListSelect(imdbListSearchResults[0]);
                                }
                              }
                            }
                          }}
                        />
                        
                        {/* Autocomplete dropdown - Modified condition */}
                        {(hasSearched && !isImportMode && !isSearchingLists) && (
                          <div className="absolute z-[100] mt-1 w-full bg-gray-700 border border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                            <ul className="py-1">
                              {/* Conditional rendering inside the dropdown */}
                              {imdbListSearchResults.length > 0 ? (
                                imdbListSearchResults.map((list, index) => (
                                  <li
                                    key={list.id || index}
                                    className="px-3 py-2 hover:bg-gray-600 cursor-pointer text-sm text-white"
                                    onClick={() => handleImdbListSelect(list)}
                                  >
                                    {list.title}
                                  </li>
                                ))
                              ) : (
                                /* Display "No results" message inside the dropdown */
                                <li className="px-3 py-2 text-sm text-amber-500 cursor-default">
                                  No results for '{lastSearchTerm}'. Try a different search or import a URL.
                                </li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex justify-between items-center mt-2">
                        {/* Title / Loading Area */} 
                        <div className="hidden md:block flex-1 overflow-hidden mr-2"> {/* Added mr-2 */} 
                          {importLoading ? (
                            <div className="text-amber-400 text-sm font-medium flex items-center gap-2">
                               <FaSpinner className="animate-spin" />
                               <span>Processing, this could take a minute...</span>
                             </div>
                          ) : listTitle ? (
                             <div className="text-green-400 text-sm font-medium truncate">
                               {isImportMode ? 'Imported:' : 'Loaded:'} {listTitle}
                             </div>
                          ) : null /* Render nothing if not loading and no title */}
                        </div>
                        
                        {/* Button */} 
                        <button 
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm whitespace-nowrap"
                          onClick={handleImportList}
                          disabled={importLoading}
                        >
                          {importLoading ? (
                            <FaSpinner className="animate-spin inline mr-2" />
                          ) : null}
                          {importLoading ? 'Loading...' : isImportMode ? 'Import' : 'Load'}
                        </button>
                      </div>
                      
                      {importError && <div className="text-red-500 mt-2 text-sm">{importError}</div>}
                      
                      {/* Mobile Title / Loading Area */} 
                      <div className="mt-2 text-sm font-medium md:hidden"> 
                        {importLoading ? (
                          <div className="text-amber-400 flex items-center justify-center gap-2">
                            <FaSpinner className="animate-spin" />
                            <span>Processing, this could take a minute...</span>
                          </div>
                        ) : listTitle ? (
                          <div className="text-green-400">
                            {isImportMode ? 'Imported:' : 'Loaded:'} {listTitle}
                          </div>
                        ) : null }
                      </div>
                    </div>
                  )}
                </div>
                
                {/* IMDb Title Search Accordion */}
                <div className="bg-gray-800 rounded-lg shadow-sm overflow-hidden">
                  <button
                    className="w-full text-left px-4 py-3 bg-gray-700 hover:bg-gray-600 focus:outline-none"
                    onClick={() => setOpenSection(openSection === 'search' ? null : 'search')}
                  >
                    <h2 className="text-lg font-bold text-white">IMDb Title Search</h2>
                  </button>
                  {openSection === 'search' && (
                    <div className="p-4">
                      <h3 className="text-sm text-gray-300 mb-3">Search for a single movie or series of movies eg: Star Wars</h3>
                      <div className="flex gap-2 mb-3">
                        <input
                          className="border border-gray-600 rounded px-2 py-1 flex-1 text-sm bg-gray-700 text-white placeholder-gray-400"
                          placeholder="Search movies or TV shows..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && performSearch()}
                        />
                        <button 
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm whitespace-nowrap search-button"
                          onClick={performSearch}
                          disabled={searchLoading}
                        >
                          {searchLoading ? 'Searching...' : 'Search'}
                        </button>
                      </div>
                      
                      {searchError && <div className="text-red-500 mb-2 text-sm">{searchError}</div>}
                    </div>
                  )}
                </div>
                
                {/* Results Controls - moved outside of the search box */}
                {searchResults.length > 0 && (
                  <div className="bg-gray-800 p-4 rounded-lg shadow-sm">
                    {/* Filter and Sort controls */}
                    {/* Modified: Always row, allow wrap */}
                    <div className="flex flex-row flex-wrap items-center gap-2 mb-3">
                      {/* Filter Input */}
                      {/* Modified: Removed explicit width, rely on flex-grow */}
                      <div className="flex-grow"> 
                        <label htmlFor="filter-input" className="sr-only">Filter results</label>
                        <input
                          id="filter-input"
                          type="text"
                          className="border border-gray-600 rounded px-2 py-1 w-full text-sm bg-gray-700 text-white placeholder-gray-400"
                          placeholder="Filter this list..."
                          value={filterText}
                          onChange={(e) => setFilterText(e.target.value)}
                        />
                      </div>
                      {/* Sort Dropdown */}
                      <div className="flex-shrink-0">
                        <label htmlFor="sort-select" className="sr-only">Sort results by</label>
                        <select
                          id="sort-select"
                          className="border border-gray-600 rounded px-2 py-1 text-sm h-full bg-gray-700 text-white w-full sm:w-auto"
                          value={`${sortCriteria.field}-${sortCriteria.direction}`}
                          onChange={handleSortChange}
                        >
                          <option value="Title-asc">Title (A-Z)</option>
                          <option value="Title-desc">Title (Z-A)</option>
                          <option value="Year-desc">Year (Newest)</option>
                          <option value="Year-asc">Year (Oldest)</option>
                        </select>
                      </div>
                      {/* ---> ADD CLEAR BUTTON HERE (Mobile Only) <--- */}
                      {isTapInterface && (
                          <button
                              onClick={handleClearSearchResultsAndStorage}
                              // Modified: Removed mt-2 sm:mt-0
                              className="ml-auto flex items-center gap-1.5 px-2 py-0.5 rounded bg-red-700 hover:bg-red-800 text-white text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Clear current search/grid results and related cache"
                          >
                              <FaTrashAlt size={10}/>
                              <span>Clear List</span>
                          </button>
                      )}
                      {/* ---> END CLEAR BUTTON (Mobile Only) <--- */}
                    </div>

                    {/* Interaction Mode Selector (Desktop only) */}
                    {/* Modified: Moved Clear button inside this conditional block */}
                    {!isTapInterface && (
                      <div className="flex items-center gap-4 text-sm mt-3"> {/* Added mt-3 for spacing */}
                        <span className="text-gray-300 font-medium">Interaction:</span>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input 
                            type="radio" 
                            name="interactionMode" 
                            value="drag" 
                            checked={!useTapInterfaceOverride} 
                            onChange={() => setUseTapInterfaceOverride(false)} 
                            className="form-radio h-4 w-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                          />
                          Drag
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input 
                            type="radio" 
                            name="interactionMode" 
                            value="tap" 
                            checked={useTapInterfaceOverride} 
                            onChange={() => setUseTapInterfaceOverride(true)} 
                            className="form-radio h-4 w-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                          />
                          Tap
                        </label>
                        {/* ---> ADD CLEAR BUTTON HERE (Desktop Only) <--- */}
                        <button
                            onClick={handleClearSearchResultsAndStorage}
                            className="ml-auto flex items-center gap-1.5 px-2 py-0.5 rounded bg-red-700 hover:bg-red-800 text-white text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Clear current search/grid results and related cache"
                        >
                            <FaTrashAlt size={10}/>
                            <span>Clear List</span>
                        </button>
                        {/* ---> END CLEAR BUTTON (Desktop Only) <--- */}
                      </div>
                    )}
                    {/* ---> REMOVE Original CLEAR BUTTON Container <--- */}
                  </div>
                )}
                
                {/* ---> Conditionally disable ImdbSearch when preloaded <--- */}
                <ImdbSearch 
                  ref={imdbSearchRef}
                  searchResults={filteredResults}
                  setSearchResults={setSearchResults} 
                  tiers={tiers} 
                  activeDrag={activeDrag} 
                  setActiveDrag={handleSetActiveDrag} 
                  cardSize={cardSize}
                  hideSearchInput={true}
                  isTapInterface={effectiveTapInterface} // Use effectiveTapInterface which considers isTapInterface and override
                  onTapItem={effectiveTapInterface && !isReadOnlyView ? handleSearchItemTap : undefined} // Disable tap if read-only
                  useHorizontalLayout={isTapInterface} // Base layout on touch device detection
                  isLoadingExternally={loadingStarter}
                  listTitle={listTitle}
                  isReadOnly={isReadOnlyView} // Pass read-only state
                />
                
                {/* Back to Top Button - Hide on mobile */}
                {showBackToTop && !effectiveTapInterface && (
                  <button
                    onClick={() => {
                      searchResultsContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="sticky bottom-4 right-4 self-end z-[100] bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-all duration-300 mr-2 mb-2"
                    title="Back to top"
                    style={{ opacity: showBackToTop ? 1 : 0 }} // Smooth fade
                  >
                    <FaArrowUp size={16} />
                  </button>
                )}
              </div>
            </div>
            
            {/* Right column: Tier Lists - Added relative positioning for toggle button */}
            <div className={`
                transition-all duration-300 ease-in-out relative
                ${isLeftColumnCollapsed
                    ? 'md:w-full' // Collapsed desktop: Full width
                    : 'md:w-7/12 lg:w-3/5 xl:w-2/3' // Expanded desktop: Original widths
                }
             `}
            >
              {/* Collapse Toggle Button (Desktop Only) */}
              <button
                onClick={() => setIsLeftColumnCollapsed(!isLeftColumnCollapsed)}
                className="hidden md:flex absolute top-7 -translate-y-0 -left-4 z-30 bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-full shadow-md border border-gray-600 items-center justify-center"
                aria-label={isLeftColumnCollapsed ? 'Expand Search Panel' : 'Collapse Search Panel'}
                title={isLeftColumnCollapsed ? 'Expand Search Panel' : 'Collapse Search Panel'}
              >
                {isLeftColumnCollapsed ? <FaChevronRight size={16} /> : <FaChevronLeft size={16} />}
              </button>

              <TierList 
                tiers={tiers} 
                setTiers={setTiers} 
                searchResults={searchResults} 
                setSearchResults={setSearchResults} 
                activeDrag={activeDrag} 
                setActiveDrag={handleSetActiveDrag}
                cardSize={cardSize}
                setCardSize={setCardSize}
                isTapInterface={isTapInterface} // <-- PASS the raw isTapInterface flag
                onTapItem={effectiveTapInterface && !isReadOnlyView ? handleTierItemTap : undefined} // Keep using effectiveTapInterface for the handler logic
                isReadOnly={isReadOnlyView} // Pass the read-only state
              />
              {/* Only show TierListShare if not read-only */}
              {!isReadOnlyView && <TierListShare tiers={tiers} />}
            </div>
          </div>
          
          <DragOverlay className="drag-overlay-visible" dropAnimation={instantDropAnimation}>
            {activeDrag ? (
              <DraggablePosterOverlay 
                activeDrag={activeDrag}
                cardSize={cardSize}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
        
        {/* Tier Selector Popup */}
        <TierSelector
          tiers={tiers}
          isOpen={tierSelectorOpen}
          onClose={handleTierSelectorClose}
          onSelectTier={handleTierSelect}
          onRemoveFromTier={sourceTierIdx !== null ? handleRemoveFromTier : undefined}
          isInTier={sourceTierIdx !== null}
          position={tapPosition}
          currentTierIdx={sourceTierIdx}
          onReorderInTier={handleReorderInTier}
        />
        
        {/* Position Selector Popup */}
        {sourceTierIdx !== null && sourceItemIdx !== null && !isReadOnlyView && ( // Add isReadOnlyView check
          <PositionSelector
            isOpen={positionSelectorOpen}
            onClose={handlePositionSelectorClose}
            onSelectPosition={handlePositionSelect}
            tierName={sourceTierIdx !== null ? tiers[sourceTierIdx]?.name : ''}
            tierColor={sourceTierIdx !== null ? tiers[sourceTierIdx]?.color : ''}
            itemCount={sourceTierIdx !== null ? tiers[sourceTierIdx]?.items.length : 0}
            currentPosition={sourceItemIdx}
            position={tapPosition}
          />
        )}
      </main>
      
      {/* Footer */}
      <footer className="bg-gray-800 text-gray-300 py-8 mt-8 flex-shrink-0">
        <div className="w-full max-w-[1920px] mx-auto px-4">
          <div className="flex flex-col items-center">
            <div className="mb-6 text-center">
              <a href="/" className="flex justify-center">
                <img 
                  src="/tiermdb-logo-transparent.png" 
                  alt="TierMDB Logo" 
                  className="h-10" 
                />
              </a>
              <p className="mt-2 text-sm">Create and share movie and TV show tier lists with ease.</p>
            </div>
            
            <div className="flex items-center justify-center space-x-4"> {/* Added space-x-4 */}
              <a href="/" className="text-sm hover:text-white transition">Home</a>
              <span className="mx-2 text-gray-500 hidden">•</span> {/* Hide default separator */}
              <a href="#" onClick={(e) => { e.preventDefault(); setShowAboutModal(true); }} className="text-sm hover:text-white transition">About</a>
              <span className="mx-2 text-gray-500 hidden">•</span> {/* Hide default separator */}
              <a href="mailto:admin@tiermdb.com" className="text-sm hover:text-white transition">Email</a>
              <span className="mx-2 text-gray-500 hidden">•</span> {/* Hide default separator */}
            </div>
          </div>
          
          <div className="mt-8 pt-6 border-t border-gray-700 text-sm text-center">
            <p>© {new Date().getFullYear()} TierMDB. All rights reserved.</p>
            <p className="mt-1 text-gray-400">Data provided by IMDb and OMDb API. This site is not affiliated with IMDb or Amazon.</p>
          </div>
        </div>
      </footer>
      
      {/* About Modal */}
      {showAboutModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
          onClick={() => setShowAboutModal(false)} // Close modal on overlay click
        >
          {/* Inner container: Apply max-height, flex-col, and stop propagation */}
          <div
            className="bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-auto relative flex flex-col max-h-[85vh]"
            onClick={(e) => e.stopPropagation()} // Prevent clicks inside modal from closing it
          >
            {/* Header section (non-scrolling) */}
            <div className="p-6 pb-0 flex-shrink-0"> {/* Adjusted padding */}
              <button
                onClick={() => setShowAboutModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white z-10" // Ensure button is above scrolling content
                aria-label="Close modal"
              >
                <FaTimes size={20} />
              </button>
              <h2 className="text-2xl font-bold text-white mb-4">About TierMDB</h2>
            </div>

            {/* Content area (scrollable) */}
            <div className="p-6 pt-2 overflow-y-auto flex-grow scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800"> {/* Added overflow, adjusted padding, scrollbar styles */}
              <div className="text-gray-300 space-y-4">
                <p>
                  I created TierMDB when I went to create a simple tier list based off already available IMDb lists and was unable to find a way to do it. 
                  All I could find is ones that required me to upload posters myself, or use premade lists that users made that were very incomplete. 
                  I made this for myself, but I hope you enjoy!
                </p>
                
                <div>
                  <h3 className="text-xl font-semibold text-white mt-6 mb-2">To do:</h3>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Add user login system so you can keep your lists and edit them as your taste change</li>
                    <li>Actor, Director, Producer search</li>
                    <li>and more!</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mt-6 mb-2">FAQ:</h3>
                  <dl className="space-y-4">
                    <dt className="font-medium text-white">How are you getting this data?</dt>
                    <dd className="pl-4">I'm using the <a href="https://www.omdbapi.com/" target="_blank" rel="noopener noreferrer">OMDb API</a> to get the data, mixed with some IMDb list scraping. The IMDb API is like $150k a month, so I'm kind of hacking it together.</dd>

                    <dt className="font-medium text-white">Can I edit a tier list after creating it?</dt>
                    <dd className="pl-4">You can create your tier list URL to save it, and then update it and then copy the new tier list URL to share if you make updates.</dd>

                    <dt className="font-medium text-white">Will you ever charge for this?</dt>
                    <dd className="pl-4">Nah</dd>
                  </dl>
                </div>
                <p className="mt-6">
                  Have feedback? Email <a href="mailto:admin@tiermdb.com" className="text-blue-400 hover:underline">admin@tiermdb.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 

// ---> NEW: Component for Drag Overlay Poster with Error Handling <---
const DraggablePosterOverlay = ({ activeDrag, cardSize }: { activeDrag: any, cardSize: string }) => {
  const [isKnownBad, setIsKnownBad] = useState(false); // <-- ADD STATE
  const posterUrl = activeDrag?.Poster;

  // Reset error state when the dragged item changes
  useEffect(() => {
    setIsKnownBad(false);
  }, [activeDrag]); // <-- ADD EFFECT TO RESET STATE

  const handleImageError = useCallback(() => {
    console.warn(`[DragOverlay Image Error] Failed to load: ${posterUrl}`);
    setIsKnownBad(true); // <-- SET STATE ON ERROR
    if (posterUrl) {
      addBadPoster(posterUrl); // <-- ADD TO CACHE ON ERROR
    }
  }, [posterUrl]);

  if (!activeDrag) return null;

  const item = activeDrag;
  const { width, height } = CARD_DIMENSIONS[cardSize] || CARD_DIMENSIONS.medium;
  const isCurrentlyKnownBad = isKnownBad || isBadPoster(posterUrl); // <-- CHECK CACHE AND STATE

  return (
    <div 
      className="draggable-poster-overlay shadow-2xl rounded overflow-hidden bg-gray-700 flex items-center justify-center"
      style={{ 
        width: `${width}px`, 
        height: `${height}px`,
        // Add transition for smoother appearance (optional)
        transition: 'transform 0.1s ease-out', 
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.15)' 
      }}
    >
      {/* Conditionally render image or fallback */}
      {posterUrl && posterUrl !== 'N/A' && !isCurrentlyKnownBad ? (
        <img 
          src={posterUrl} 
          alt={item.Title} 
          className="w-full h-full object-cover pointer-events-none"
          onError={handleImageError} // Use the error handler
          // Add key to force re-render if item changes (though useEffect handles reset)
          key={item.imdbID} 
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-300 p-1 text-xs text-center font-semibold">
          {item.Title || 'No Poster Available'}
        </div>
      )}
    </div>
  );
};